import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import './App.css';

// ============================================================================
// Authentication Context
// ============================================================================

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

// ============================================================================
// API Helper with Authentication
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || '/api';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('arrwise_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('arrwise_token', token);
    } else {
      localStorage.removeItem('arrwise_token');
    }
  }

  getToken() {
    return this.token;
  }

  async fetch(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.setToken(null);
      window.dispatchEvent(new CustomEvent('auth:logout'));
      throw new Error('Session expired. Please log in again.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = errorData.detail;
      let message;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
      } else {
        message = `API error: ${response.status}`;
      }
      throw new Error(message);
    }

    return response.json();
  }

  async login(username, password) {
    const data = await this.fetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    this.setToken(data.token);
    return data;
  }

  logout() {
    this.setToken(null);
  }
}

const api = new ApiClient();

// ============================================================================
// Login Component
// ============================================================================

function LoginPage({ onLogin, setupRequired, onSetupComplete }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFirstSetup, setIsFirstSetup] = useState(setupRequired);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await api.login(username, password);
      
      // Check if this is first login (setup not complete)
      const setupStatus = await api.fetch('/auth/setup-status');
      if (!setupStatus.setup_complete) {
        setIsFirstSetup(true);
      } else {
        onLogin(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await api.fetch('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: password,
          new_password: newPassword,
        }),
      });

      // Re-login with new password
      const data = await api.login(username, newPassword);
      onLogin(data.user);
      if (onSetupComplete) onSetupComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (isFirstSetup && api.getToken()) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <span className="login-logo">⚡</span>
            <h1>Welcome to ArrWise</h1>
            <p>Please set a new password for your admin account</p>
          </div>

          <form onSubmit={handleSetupPassword} className="login-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={8}
              />
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? 'Setting up...' : 'Set Password & Continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <span className="login-logo">⚡</span>
          <h1>ArrWise</h1>
          <p>Intelligent Download Manager</p>
        </div>

        {setupRequired && (
          <div className="setup-notice">
            <strong>First Time Setup</strong>
            <p>Check the console output for your default admin password</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              required
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Logging in...' : 'Log In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Logs Viewer (Settings > Logs)
// ============================================================================

function LogsViewer() {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [levelFilter, setLevelFilter] = useState('');
  const [lineCount, setLineCount] = useState(500);
  const [search, setSearch] = useState('');
  const [runtimeLevel, setRuntimeLevel] = useState('INFO');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [fileSize, setFileSize] = useState(0);
  const [message, setMessage] = useState(null);
  const logBoxRef = React.useRef(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ lines: lineCount });
      if (levelFilter) params.append('level', levelFilter);
      if (search.trim()) params.append('search', search.trim());
      const data = await api.fetch(`/logs?${params}`);
      setLines(data.lines || []);
      setFileSize(data.file_size_mb || 0);
      if (data.current_level) setRuntimeLevel(data.current_level);
      // Scroll to the newest entries
      setTimeout(() => {
        if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
      }, 50);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load logs: ' + err.message });
    } finally {
      setLoading(false);
    }
  }, [lineCount, levelFilter, search]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadLogs, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadLogs]);

  const changeRuntimeLevel = async (level) => {
    try {
      const r = await api.fetch(`/logs/level?level=${level}`, { method: 'POST' });
      setRuntimeLevel(level);
      setMessage({ type: 'success', text: r.message });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to change log level: ' + err.message });
    }
  };

  const [exporting, setExporting] = useState(false);

  const exportLogs = async (full = false) => {
    if (full && fileSize > 20 && !window.confirm(
      `The full log file is ${fileSize} MB — this may take a while to download. Continue?`
    )) return;
    setExporting(true);
    setMessage(null);
    try {
      // Get a one-time token, then hand the download to the BROWSER via a
      // plain link — it streams with the native download UI instead of
      // buffering the whole file in JS (which hung/timed out on big files)
      const t = await api.fetch('/logs/download-token', { method: 'POST' });
      const a = document.createElement('a');
      a.href = `${API_BASE}/logs/download?dl_token=${encodeURIComponent(t.token)}&full=${full}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setMessage({ type: 'success', text: 'Download started — check your browser downloads bar' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to export logs: ' + err.message });
    } finally {
      setExporting(false);
    }
  };

  const lineClass = (line) => {
    if (line.includes(' - ERROR - ') || line.includes(' - CRITICAL - ')) return 'log-line error';
    if (line.includes(' - WARNING - ')) return 'log-line warning';
    if (line.includes(' - DEBUG - ')) return 'log-line debug';
    return 'log-line';
  };

  return (
    <div className="settings-section logs-section">
      <h3>Application Logs</h3>
      <p className="section-description">
        View and export ArrWise logs without touching Docker. Tip: filter with
        "[Scan]" to follow scan decisions, or "[Cleanup]" for queue cleanup.
      </p>

      {message && (
        <div className={`cleanup-result ${message.type}`}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
          <button className="dismiss-btn" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="logs-toolbar">
        <label>Log level:</label>
        <select value={runtimeLevel} onChange={(e) => changeRuntimeLevel(e.target.value)} title="Runtime logging verbosity (resets to INFO on restart)">
          <option value="DEBUG">DEBUG (verbose)</option>
          <option value="INFO">INFO</option>
          <option value="WARNING">WARNING</option>
          <option value="ERROR">ERROR</option>
        </select>

        <label>View:</label>
        <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
          <option value="">All entries</option>
          <option value="INFO">INFO and above</option>
          <option value="WARNING">WARNING and above</option>
          <option value="ERROR">ERROR only</option>
        </select>

        <select value={lineCount} onChange={(e) => setLineCount(Number(e.target.value))}>
          <option value={200}>Last 200</option>
          <option value={500}>Last 500</option>
          <option value={1000}>Last 1000</option>
          <option value={2000}>Last 2000</option>
        </select>

        <input
          type="text"
          className="logs-search"
          placeholder="🔍 Filter (e.g. [Scan])"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') loadLogs(); }}
        />
      </div>

      <div className="logs-toolbar">
        <button className="logs-btn" onClick={loadLogs} disabled={loading}>
          {loading ? '⏳ Loading…' : '🔄 Refresh'}
        </button>
        <label className="logs-autorefresh">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (5s)
        </label>
        <button className="logs-btn" onClick={() => exportLogs(false)} disabled={exporting}>
          {exporting ? '⏳ Preparing…' : '📥 Export recent'}
        </button>
        <button className="logs-btn" onClick={() => exportLogs(true)} disabled={exporting}>
          📦 Export full{fileSize > 0 ? ` (${fileSize} MB)` : ''}
        </button>
      </div>

      <div className="logs-viewer" ref={logBoxRef}>
        {lines.length === 0 ? (
          <div className="logs-empty">{loading ? 'Loading…' : 'No log entries match the current filter'}</div>
        ) : (
          lines.map((line, i) => (
            <div key={i} className={lineClass(line)}>{line}</div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Settings Component with Connection Configuration
// ============================================================================

function SettingsPage({ onBack }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ sonarr: false, radarr: false });
  const [testResults, setTestResults] = useState({ sonarr: null, radarr: null });
  const [message, setMessage] = useState(null);
  const [activeSection, setActiveSection] = useState('connections');

  // Form state for connection settings (separate from main settings to allow editing before save)
  const [sonarrUrl, setSonarrUrl] = useState('');
  const [sonarrApiKey, setSonarrApiKey] = useState('');
  const [sonarrEnabled, setSonarrEnabled] = useState(true);
  const [radarrUrl, setRadarrUrl] = useState('');
  const [radarrApiKey, setRadarrApiKey] = useState('');
  const [radarrEnabled, setRadarrEnabled] = useState(true);

  // Tolerance settings
  const [autoTolerance, setAutoTolerance] = useState(10);
  const [manualThreshold, setManualThreshold] = useState(50);
  const [largeThreshold, setLargeThreshold] = useState(200);

  // Other settings
  const [scanInterval, setScanInterval] = useState(60);
  const [maxItemsPerScan, setMaxItemsPerScan] = useState(50);
  const [preferUsenet, setPreferUsenet] = useState(true);
  const [autoBlocklist, setAutoBlocklist] = useState(true);
  const [allowSdFallback, setAllowSdFallback] = useState(true);
  const [sdFallbackAge, setSdFallbackAge] = useState(15);
  const [sdHandlingMode, setSdHandlingMode] = useState('require_approval');

  // New settings that were missing from UI
  const [automaticScansEnabled, setAutomaticScansEnabled] = useState(true);
  const [skipUnreleasedContent, setSkipUnreleasedContent] = useState(true);
  const [autoCleanupEnabled, setAutoCleanupEnabled] = useState(false);
  const [cleanupIntervalHours, setCleanupIntervalHours] = useState(6);
  const [autoRetryFailed, setAutoRetryFailed] = useState(true);
  const [retrySizeTolerance, setRetrySizeTolerance] = useState(10);
  const [maxAutoRetries, setMaxAutoRetries] = useState(3);
  const [disableTorrents, setDisableTorrents] = useState(false);
  const [requireTorrentApproval, setRequireTorrentApproval] = useState(false);
  const [allow4kFallback, setAllow4kFallback] = useState(true);
  const [alwaysApprove4k, setAlwaysApprove4k] = useState(true);
  const [sizeAnomalyThreshold, setSizeAnomalyThreshold] = useState(50);
  const [trackFailurePatterns, setTrackFailurePatterns] = useState(true);
  const [failurePatternThreshold, setFailurePatternThreshold] = useState(3);
  const [maxRetryAttempts, setMaxRetryAttempts] = useState(3);
  const [autoPurgeMode, setAutoPurgeMode] = useState('disabled');
  const [autoPurgeDays, setAutoPurgeDays] = useState(30);
  const [autoPurgeScope, setAutoPurgeScope] = useState('completed');
  const [preserveBlocklistOnPurge, setPreserveBlocklistOnPurge] = useState(true);
  const [appUrl, setAppUrl] = useState('http://10.69.20.12:3000');
  
  // Purge actions
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.fetch('/settings/full');
      setSettings(data);

      // Populate form fields
      setSonarrUrl(data.sonarr_url || '');
      setSonarrApiKey(data.sonarr_api_key || '');
      setSonarrEnabled(data.sonarr_enabled !== false);
      setRadarrUrl(data.radarr_url || '');
      setRadarrApiKey(data.radarr_api_key || '');
      setRadarrEnabled(data.radarr_enabled !== false);
      setAutoTolerance(data.auto_approve_tolerance_percent || 10);
      setManualThreshold(data.manual_approval_threshold_percent || 50);
      setLargeThreshold(data.large_override_threshold_percent || 200);
      setScanInterval(data.scan_interval_minutes || 60);
      setMaxItemsPerScan(data.max_items_per_scan || 50);
      setPreferUsenet(data.prefer_usenet !== false);
      setAutoBlocklist(data.auto_blocklist_failed !== false);
      setAllowSdFallback(data.allow_sd_fallback !== false);
      setSdFallbackAge(data.sd_fallback_age_years || 15);
      setSdHandlingMode(data.sd_handling_mode || 'require_approval');
      
      // New settings
      setAutomaticScansEnabled(data.automatic_scans_enabled !== false);
      setSkipUnreleasedContent(data.skip_unreleased_content !== false);
      setAutoCleanupEnabled(data.auto_cleanup_enabled === true);
      setCleanupIntervalHours(data.cleanup_interval_hours || 6);
      setAutoRetryFailed(data.auto_retry_failed !== false);
      setRetrySizeTolerance(data.retry_size_tolerance_percent ?? 10);
      setMaxAutoRetries(data.max_auto_retries ?? 3);
      setDisableTorrents(data.disable_torrents === true);
      setRequireTorrentApproval(data.require_torrent_approval === true);
      setAllow4kFallback(data.allow_4k_fallback !== false);
      setAlwaysApprove4k(data.always_approve_4k !== false);
      setSizeAnomalyThreshold(data.size_anomaly_threshold_percent || 50);
      setTrackFailurePatterns(data.track_failure_patterns !== false);
      setFailurePatternThreshold(data.failure_pattern_threshold || 3);
      setMaxRetryAttempts(data.max_retry_attempts || 3);
      setAutoPurgeMode(data.auto_purge_mode || 'disabled');
      setAutoPurgeDays(data.auto_purge_days || 30);
      setAutoPurgeScope(data.auto_purge_scope || 'completed');
      setPreserveBlocklistOnPurge(data.preserve_blocklist_on_purge !== false);
      setAppUrl(data.app_url || 'http://10.69.20.12:3000');
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (service) => {
    const url = service === 'sonarr' ? sonarrUrl : radarrUrl;
    const apiKey = service === 'sonarr' ? sonarrApiKey : radarrApiKey;

    if (!url || !apiKey) {
      setTestResults(prev => ({
        ...prev,
        [service]: { success: false, message: 'URL and API key are required' }
      }));
      return;
    }

    setTesting(prev => ({ ...prev, [service]: true }));
    setTestResults(prev => ({ ...prev, [service]: null }));

    try {
      const result = await api.fetch(`/settings/test-connection?service=${service}`, {
        method: 'POST',
        body: JSON.stringify({ url, api_key: apiKey }),
      });
      setTestResults(prev => ({ ...prev, [service]: result }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [service]: { success: false, message: err.message }
      }));
    } finally {
      setTesting(prev => ({ ...prev, [service]: false }));
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage(null);

    try {
      await api.fetch('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          sonarr_url: sonarrUrl,
          sonarr_api_key: sonarrApiKey,
          sonarr_enabled: sonarrEnabled,
          radarr_url: radarrUrl,
          radarr_api_key: radarrApiKey,
          radarr_enabled: radarrEnabled,
          auto_approve_tolerance_percent: autoTolerance,
          manual_approval_threshold_percent: manualThreshold,
          large_override_threshold_percent: largeThreshold,
          scan_interval_minutes: scanInterval,
          max_items_per_scan: maxItemsPerScan,
          prefer_usenet: preferUsenet,
          auto_blocklist_failed: autoBlocklist,
          allow_sd_fallback: allowSdFallback,
          sd_fallback_age_years: sdFallbackAge,
          sd_handling_mode: sdHandlingMode,
          // New settings
          automatic_scans_enabled: automaticScansEnabled,
          skip_unreleased_content: skipUnreleasedContent,
          auto_cleanup_enabled: autoCleanupEnabled,
          cleanup_interval_hours: cleanupIntervalHours,
          auto_retry_failed: autoRetryFailed,
          retry_size_tolerance_percent: retrySizeTolerance,
          max_auto_retries: maxAutoRetries,
          disable_torrents: disableTorrents,
          require_torrent_approval: requireTorrentApproval,
          allow_4k_fallback: allow4kFallback,
          always_approve_4k: alwaysApprove4k,
          size_anomaly_threshold_percent: sizeAnomalyThreshold,
          track_failure_patterns: trackFailurePatterns,
          failure_pattern_threshold: failurePatternThreshold,
          max_retry_attempts: maxRetryAttempts,
          auto_purge_mode: autoPurgeMode,
          auto_purge_days: autoPurgeDays,
          auto_purge_scope: autoPurgeScope,
          preserve_blocklist_on_purge: preserveBlocklistOnPurge,
          app_url: appUrl,
        }),
      });
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save: ' + err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <button className="back-btn" onClick={onBack}>← Back</button>
        <h2>Settings</h2>
        <button className="save-btn" onClick={saveSettings} disabled={saving}>
          {saving ? 'Saving...' : 'Save All'}
        </button>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {purgeResult && (
        <div className={`message ${purgeResult.type}`}>
          {purgeResult.text}
          <button className="dismiss-inline" onClick={() => setPurgeResult(null)}>×</button>
        </div>
      )}

      <div className="settings-nav">
        <button
          className={activeSection === 'connections' ? 'active' : ''}
          onClick={() => setActiveSection('connections')}
        >
          Connections
        </button>
        <button
          className={activeSection === 'tolerance' ? 'active' : ''}
          onClick={() => setActiveSection('tolerance')}
        >
          Tolerance
        </button>
        <button
          className={activeSection === 'scanning' ? 'active' : ''}
          onClick={() => setActiveSection('scanning')}
        >
          Scanning
        </button>
        <button
          className={activeSection === 'behavior' ? 'active' : ''}
          onClick={() => setActiveSection('behavior')}
        >
          Behavior
        </button>
        <button
          className={activeSection === 'quality' ? 'active' : ''}
          onClick={() => setActiveSection('quality')}
        >
          Quality
        </button>
        <button
          className={activeSection === 'purge' ? 'active' : ''}
          onClick={() => setActiveSection('purge')}
        >
          Data Purge
        </button>
        <button
          className={activeSection === 'logs' ? 'active' : ''}
          onClick={() => setActiveSection('logs')}
        >
          Logs
        </button>
      </div>

      <div className="settings-content">
        {activeSection === 'connections' && (
          <>
          <div className="settings-section">
            <h3>Sonarr Connection</h3>
            <p className="section-description">
              Connect to your Sonarr instance to manage TV show downloads.
              ArrWise will use Sonarr's quality profiles to determine acceptable file sizes.
            </p>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={sonarrEnabled}
                  onChange={(e) => setSonarrEnabled(e.target.checked)}
                />
                Enable Sonarr Integration
              </label>
            </div>

            <div className="form-group">
              <label>Sonarr URL</label>
              <input
                type="url"
                value={sonarrUrl}
                onChange={(e) => setSonarrUrl(e.target.value)}
                placeholder="http://192.168.1.100:8989"
                disabled={!sonarrEnabled}
              />
              <small>Include the port number (usually 8989)</small>
            </div>

            <div className="form-group">
              <label>API Key</label>
              <input
                type="password"
                value={sonarrApiKey}
                onChange={(e) => setSonarrApiKey(e.target.value)}
                placeholder="Your Sonarr API key"
                disabled={!sonarrEnabled}
              />
              <small>Found in Sonarr → Settings → General → Security</small>
            </div>

            <button
              className="test-btn"
              onClick={() => testConnection('sonarr')}
              disabled={testing.sonarr || !sonarrEnabled}
            >
              {testing.sonarr ? 'Testing...' : 'Test Connection'}
            </button>

            {testResults.sonarr && (
              <div className={`test-result ${testResults.sonarr.success ? 'success' : 'error'}`}>
                {testResults.sonarr.success ? '✓ ' : '✗ '}
                {testResults.sonarr.message}
              </div>
            )}

            <hr />

            <h3>Radarr Connection</h3>
            <p className="section-description">
              Connect to your Radarr instance to manage movie downloads.
            </p>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={radarrEnabled}
                  onChange={(e) => setRadarrEnabled(e.target.checked)}
                />
                Enable Radarr Integration
              </label>
            </div>

            <div className="form-group">
              <label>Radarr URL</label>
              <input
                type="url"
                value={radarrUrl}
                onChange={(e) => setRadarrUrl(e.target.value)}
                placeholder="http://192.168.1.100:7878"
                disabled={!radarrEnabled}
              />
              <small>Include the port number (usually 7878)</small>
            </div>

            <div className="form-group">
              <label>API Key</label>
              <input
                type="password"
                value={radarrApiKey}
                onChange={(e) => setRadarrApiKey(e.target.value)}
                placeholder="Your Radarr API key"
                disabled={!radarrEnabled}
              />
              <small>Found in Radarr → Settings → General → Security</small>
            </div>

            <button
              className="test-btn"
              onClick={() => testConnection('radarr')}
              disabled={testing.radarr || !radarrEnabled}
            >
              {testing.radarr ? 'Testing...' : 'Test Connection'}
            </button>

            {testResults.radarr && (
              <div className={`test-result ${testResults.radarr.success ? 'success' : 'error'}`}>
                {testResults.radarr.success ? '✓ ' : '✗ '}
                {testResults.radarr.message}
              </div>
            )}
          </div>

          <div className="settings-section" style={{marginTop: '1.5rem'}}>
            <h3>App URL</h3>
            <p className="section-description">
              Set the external URL for ArrWise. Used for PWA installation and future notifications.
              For PWA install to work, this must be an HTTPS URL (e.g. https://arrwise.yourdomain.com).
            </p>
            <div className="form-group">
              <label>External URL</label>
              <input
                type="url"
                value={appUrl}
                onChange={(e) => setAppUrl(e.target.value)}
                placeholder="https://arrwise.example.com"
              />
              <small>
                {appUrl && appUrl.startsWith('https://') 
                  ? '✅ HTTPS — PWA install will work'
                  : '⚠️ HTTP only — PWA install requires HTTPS. Use "Add to Home Screen" from your browser menu as a workaround.'}
              </small>
            </div>
          </div>
          </>
        )}

        {activeSection === 'tolerance' && (
          <div className="settings-section">
            <h3>Size Tolerance Settings</h3>
            <p className="section-description">
              These settings control how ArrWise handles files that exceed your Sonarr/Radarr size limits.
              Your quality profiles already define acceptable size ranges - these settings add flexibility
              for edge cases where nothing is available within those ranges.
            </p>

            <div className="form-group">
              <label>Auto-Approve Tolerance: {autoTolerance}%</label>
              <input
                type="range"
                min="0"
                max="50"
                value={autoTolerance}
                onChange={(e) => setAutoTolerance(parseInt(e.target.value))}
              />
              <small>
                Files up to {autoTolerance}% over your quality profile's max size will be 
                automatically downloaded. Example: if your limit is 1.5GB, files up to 
                {(1.5 * (1 + autoTolerance/100)).toFixed(2)}GB will auto-download.
              </small>
            </div>

            <div className="form-group">
              <label>Manual Approval Threshold: {manualThreshold}%</label>
              <input
                type="range"
                min="25"
                max="300"
                step="25"
                value={manualThreshold}
                onChange={(e) => setManualThreshold(parseInt(e.target.value))}
              />
              <small>
                Files between {autoTolerance}% and {manualThreshold}% over will be queued
                for your manual approval. These are larger than ideal but might be your only option.
              </small>
            </div>

            <div className="form-group">
              <label>Large File Warning: {largeThreshold}%</label>
              <input
                type="range"
                min="100"
                max="500"
                step="50"
                value={largeThreshold}
                onChange={(e) => setLargeThreshold(parseInt(e.target.value))}
              />
              <small>
                Files more than {largeThreshold}% over the limit will show a warning
                in the approval queue. These are significantly larger than expected.
              </small>
            </div>

            <div className="tolerance-visualization">
              <h4>How It Works</h4>
              <div className="tolerance-chart">
                <div className="zone within" style={{flex: 1}}>
                  <span>Within Limits</span>
                  <small>Sonarr/Radarr handles</small>
                </div>
                <div className="zone auto" style={{flex: autoTolerance/100}}>
                  <span>Auto-Download</span>
                  <small>0-{autoTolerance}% over</small>
                </div>
                <div className="zone manual" style={{flex: (manualThreshold - autoTolerance)/100}}>
                  <span>Manual Approval</span>
                  <small>{autoTolerance}-{manualThreshold}% over</small>
                </div>
                <div className="zone warning" style={{flex: 0.5}}>
                  <span>Warning</span>
                  <small>{'>'}{largeThreshold}% over</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'scanning' && (
          <div className="settings-section">
            <h3>Scanning Settings</h3>
            
            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={automaticScansEnabled}
                  onChange={(e) => setAutomaticScansEnabled(e.target.checked)}
                />
                Enable Automatic Scans
              </label>
              <small>
                When disabled, ArrWise will NOT automatically scan for missing content.
                You can still trigger manual scans from the dashboard.
              </small>
            </div>

            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={skipUnreleasedContent}
                  onChange={(e) => setSkipUnreleasedContent(e.target.checked)}
                />
                Skip Unreleased Content
              </label>
              <small>
                Don't scan for movies or episodes that haven't been released yet.
                Sonarr/Radarr know the release dates from TMDb/TVDb.
              </small>
            </div>
            
            <div className="form-group">
              <label>Scan Interval: {scanInterval} minutes</label>
              <input
                type="range"
                min="15"
                max="360"
                step="15"
                value={scanInterval}
                onChange={(e) => setScanInterval(parseInt(e.target.value))}
              />
              <small>
                How often ArrWise checks for missing content. Lower values mean
                faster response but more API calls to your indexers.
              </small>
            </div>

            <div className="form-group">
              <label>Max Items Per Scan: {maxItemsPerScan}</label>
              <input
                type="range"
                min="10"
                max="200"
                step="10"
                value={maxItemsPerScan}
                onChange={(e) => setMaxItemsPerScan(parseInt(e.target.value))}
              />
              <small>
                Maximum number of missing items to process per scan.
                This prevents overwhelming your indexers.
              </small>
            </div>

            <h3 style={{marginTop: '1.5rem'}}>Queue Cleanup</h3>

            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={autoCleanupEnabled}
                  onChange={(e) => setAutoCleanupEnabled(e.target.checked)}
                />
                Enable Automatic Queue Cleanup
              </label>
              <small>
                Periodically runs Smart Queue Cleanup on both Sonarr and Radarr.
                Removes stuck items, retries valid imports, and blocklists mismatched files.
              </small>
            </div>

            {autoCleanupEnabled && (
              <div className="form-group">
                <label>Cleanup Interval: every {cleanupIntervalHours} hour{cleanupIntervalHours !== 1 ? 's' : ''}</label>
                <input
                  type="range"
                  min="1"
                  max="24"
                  step="1"
                  value={cleanupIntervalHours}
                  onChange={(e) => setCleanupIntervalHours(parseInt(e.target.value))}
                />
                <small>
                  How often to automatically clean up stuck queue items.
                </small>
              </div>
            )}

            <h3 style={{marginTop: '1.5rem'}}>Download Retry</h3>

            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={autoRetryFailed}
                  onChange={(e) => setAutoRetryFailed(e.target.checked)}
                />
                Auto-Retry Failed Downloads
              </label>
              <small>
                When a download fails, automatically blocklist it and grab a similarly-sized
                alternative. If nothing similar is found, escalates back to the approval queue
                with a note explaining the failure history.
              </small>
            </div>

            {autoRetryFailed && (
              <>
                <div className="form-group">
                  <label>Size Match Tolerance: ±{retrySizeTolerance}%</label>
                  <input
                    type="range"
                    min="5"
                    max="50"
                    step="5"
                    value={retrySizeTolerance}
                    onChange={(e) => setRetrySizeTolerance(parseInt(e.target.value))}
                  />
                  <small>
                    How close in size an alternative must be to auto-approve without asking.
                    Files outside this range require manual approval.
                  </small>
                </div>

                <div className="form-group">
                  <label>Max Auto-Retry Attempts: {maxAutoRetries}</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={maxAutoRetries}
                    onChange={(e) => setMaxAutoRetries(parseInt(e.target.value))}
                  />
                  <small>
                    Maximum number of similar-sized alternatives to try before giving up
                    or escalating to manual approval.
                  </small>
                </div>
              </>
            )}
          </div>
        )}

        {activeSection === 'behavior' && (
          <div className="settings-section">
            <h3>Protocol Preferences</h3>
            
            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={preferUsenet}
                  onChange={(e) => setPreferUsenet(e.target.checked)}
                />
                Prefer Usenet over Torrents
              </label>
              <small>
                When enabled, ArrWise will only consider torrent releases if no
                usenet releases are available.
              </small>
            </div>

            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={requireTorrentApproval}
                  onChange={(e) => setRequireTorrentApproval(e.target.checked)}
                />
                ✋ Require Approval for All Torrents
              </label>
              <small>
                When enabled, no torrent is ever grabbed automatically — every
                torrent decision goes to the Approvals list instead. Toggle on
                and off whenever you want to gate a torrent pass. Has no effect
                while torrents are disabled completely.
              </small>
            </div>

            <div className="form-group toggle">
              <label className={disableTorrents ? 'toggle-active-warning' : ''}>
                <input
                  type="checkbox"
                  checked={disableTorrents}
                  onChange={(e) => setDisableTorrents(e.target.checked)}
                />
                🚫 Disable Torrents Completely
              </label>
              <small>
                When enabled, torrents are completely ignored during all scans.
                Only Usenet (NZB) releases will be considered. Toggle this off
                when you want to do a torrent pass after clearing Usenet grabs.
              </small>
            </div>

            <h3>Failure Handling</h3>

            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={autoBlocklist}
                  onChange={(e) => setAutoBlocklist(e.target.checked)}
                />
                Auto-Blocklist Failed Downloads
              </label>
              <small>
                Automatically blocklist releases that fail to download and try
                the next best option.
              </small>
            </div>

            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={trackFailurePatterns}
                  onChange={(e) => setTrackFailurePatterns(e.target.checked)}
                />
                Track Failed Download Patterns
              </label>
              <small>
                Remember which indexers and release groups consistently fail.
                Deprioritize them but still try as a last resort.
              </small>
            </div>

            {trackFailurePatterns && (
              <>
                <div className="form-group">
                  <label>Failure Pattern Threshold: {failurePatternThreshold} failures</label>
                  <input
                    type="range"
                    min="2"
                    max="10"
                    value={failurePatternThreshold}
                    onChange={(e) => setFailurePatternThreshold(parseInt(e.target.value))}
                  />
                  <small>
                    How many failures from the same indexer/group before deprioritizing it.
                  </small>
                </div>
              </>
            )}

            <div className="form-group">
              <label>Max Retry Attempts: {maxRetryAttempts}</label>
              <input
                type="range"
                min="1"
                max="10"
                value={maxRetryAttempts}
                onChange={(e) => setMaxRetryAttempts(parseInt(e.target.value))}
              />
              <small>
                Maximum number of times to retry a failed download before giving up.
              </small>
            </div>

            <h3>Quality Fallback</h3>

            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={allowSdFallback}
                  onChange={(e) => setAllowSdFallback(e.target.checked)}
                />
                Allow SD Fallback for Older Content
              </label>
              <small>
                When HD is requested but unavailable for older content, allow
                SD quality as an automatic fallback.
              </small>
            </div>

            {allowSdFallback && (
              <div className="form-group">
                <label>SD Fallback Age: {sdFallbackAge} years</label>
                <input
                  type="range"
                  min="5"
                  max="30"
                  value={sdFallbackAge}
                  onChange={(e) => setSdFallbackAge(parseInt(e.target.value))}
                />
                <small>
                  Content older than {sdFallbackAge} years will automatically
                  use SD quality if HD isn't available.
                </small>
              </div>
            )}

            {allowSdFallback && (
              <div className="form-group">
                <label>When SD is the Only Quality Available:</label>
                <select
                  value={sdHandlingMode}
                  onChange={(e) => setSdHandlingMode(e.target.value)}
                >
                  <option value="auto_grab">Auto-grab SD (no review)</option>
                  <option value="require_approval">Require manual approval</option>
                  <option value="try_torrent_first">Check for HD torrents first, then require approval</option>
                </select>
                <small>
                  {sdHandlingMode === 'auto_grab' && 'SD releases will be downloaded automatically when no HD is available.'}
                  {sdHandlingMode === 'require_approval' && 'All SD-only releases will be queued for your review before downloading.'}
                  {sdHandlingMode === 'try_torrent_first' && 'When only SD is on usenet, checks if HD torrents exist and queues for approval so you can choose.'}
                </small>
              </div>
            )}
          </div>
        )}

        {activeSection === 'quality' && (
          <div className="settings-section">
            <h3>4K Handling</h3>
            
            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={allow4kFallback}
                  onChange={(e) => setAllow4kFallback(e.target.checked)}
                />
                Allow 4K as Last Resort
              </label>
              <small>
                Only consider 4K releases when absolutely no 720p or 1080p options exist.
                4K will never be the first choice.
              </small>
            </div>

            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={alwaysApprove4k}
                  onChange={(e) => setAlwaysApprove4k(e.target.checked)}
                />
                Always Require Manual Approval for 4K
              </label>
              <small>
                When enabled, 4K content will NEVER auto-download — it always goes
                to the approval queue for your review.
              </small>
            </div>

            <h3>Size Anomaly Detection</h3>

            <div className="form-group">
              <label>Anomaly Threshold: {sizeAnomalyThreshold}% of expected minimum</label>
              <input
                type="range"
                min="20"
                max="80"
                step="5"
                value={sizeAnomalyThreshold}
                onChange={(e) => setSizeAnomalyThreshold(parseInt(e.target.value))}
              />
              <small>
                Flag releases smaller than {sizeAnomalyThreshold}% of the expected minimum size
                as likely fake or corrupted. These will be skipped automatically.
              </small>
            </div>
          </div>
        )}

        {activeSection === 'purge' && (
          <div className="settings-section">
            <h3>Auto-Purge Settings</h3>
            <p className="section-description">
              Automatically clean up old data to keep the database lean.
              Blocklist data is always preserved so ArrWise won't retry bad releases.
            </p>

            <div className="form-group">
              <label>Auto-Purge Mode</label>
              <select
                value={autoPurgeMode}
                onChange={(e) => setAutoPurgeMode(e.target.value)}
                className="settings-select"
              >
                <option value="disabled">Disabled</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom (days)</option>
              </select>
            </div>

            {autoPurgeMode === 'custom' && (
              <div className="form-group">
                <label>Keep Data For: {autoPurgeDays} days</label>
                <input
                  type="range"
                  min="7"
                  max="365"
                  step="7"
                  value={autoPurgeDays}
                  onChange={(e) => setAutoPurgeDays(parseInt(e.target.value))}
                />
              </div>
            )}

            <div className="form-group">
              <label>Purge Scope</label>
              <select
                value={autoPurgeScope}
                onChange={(e) => setAutoPurgeScope(e.target.value)}
                className="settings-select"
              >
                <option value="completed">Completed items only</option>
                <option value="all">All items (completed + failed)</option>
              </select>
              <small>
                Choose what gets purged. Failed items contain useful diagnostic info.
              </small>
            </div>

            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={preserveBlocklistOnPurge}
                  onChange={(e) => setPreserveBlocklistOnPurge(e.target.checked)}
                />
                Preserve Blocklist Data on Purge
              </label>
              <small>
                Always keep blocklist records even when purging. This prevents
                ArrWise from retrying releases that were previously bad.
              </small>
            </div>

            <hr />

            <h3>Manual Purge</h3>
            <p className="section-description">
              Immediately clear data from specific sections. This cannot be undone.
            </p>

            <div className="purge-buttons">
              <button 
                className="purge-btn"
                disabled={purging}
                onClick={async () => {
                  if (!window.confirm('Purge all completed activity older than 30 days?')) return;
                  setPurging(true);
                  try {
                    const r = await api.fetch('/purge/activity', { method: 'POST', body: JSON.stringify({ days: 30, scope: 'completed' }) });
                    setPurgeResult({ type: 'success', text: `Purged ${r.deleted} activity records` });
                  } catch (e) { setPurgeResult({ type: 'error', text: e.message }); }
                  finally { setPurging(false); }
                }}
              >
                🗑️ Purge Activity (30d+)
              </button>
              
              <button 
                className="purge-btn"
                disabled={purging}
                onClick={async () => {
                  if (!window.confirm('Purge all completed download history older than 30 days?')) return;
                  setPurging(true);
                  try {
                    const r = await api.fetch('/purge/downloads', { method: 'POST', body: JSON.stringify({ days: 30, scope: 'completed' }) });
                    setPurgeResult({ type: 'success', text: `Purged ${r.deleted} download records` });
                  } catch (e) { setPurgeResult({ type: 'error', text: e.message }); }
                  finally { setPurging(false); }
                }}
              >
                🗑️ Purge Downloads (30d+)
              </button>

              <button 
                className="purge-btn"
                disabled={purging}
                onClick={async () => {
                  if (!window.confirm('Purge all missing reports older than 30 days?')) return;
                  setPurging(true);
                  try {
                    const r = await api.fetch('/purge/missing', { method: 'POST', body: JSON.stringify({ days: 30 }) });
                    setPurgeResult({ type: 'success', text: `Purged ${r.deleted} missing reports` });
                  } catch (e) { setPurgeResult({ type: 'error', text: e.message }); }
                  finally { setPurging(false); }
                }}
              >
                🗑️ Purge Missing (30d+)
              </button>

              <button 
                className="purge-btn"
                disabled={purging}
                onClick={async () => {
                  if (!window.confirm('Purge all processed approvals?')) return;
                  setPurging(true);
                  try {
                    const r = await api.fetch('/purge/approvals', { method: 'POST' });
                    setPurgeResult({ type: 'success', text: `Purged ${r.deleted} processed approvals` });
                  } catch (e) { setPurgeResult({ type: 'error', text: e.message }); }
                  finally { setPurging(false); }
                }}
              >
                🗑️ Purge Processed Approvals
              </button>

              <button 
                className="purge-btn danger"
                disabled={purging}
                onClick={async () => {
                  if (!window.confirm('⚠️ Purge ALL data older than 30 days? Blocklist data will be preserved.')) return;
                  setPurging(true);
                  try {
                    const r = await api.fetch('/purge/all', { method: 'POST', body: JSON.stringify({ days: 30 }) });
                    setPurgeResult({ type: 'success', text: `Purged all data: ${JSON.stringify(r.deleted)}` });
                  } catch (e) { setPurgeResult({ type: 'error', text: e.message }); }
                  finally { setPurging(false); }
                }}
              >
                ⚠️ Purge All (30d+)
              </button>
            </div>
          </div>
        )}

        {activeSection === 'logs' && <LogsViewer />}
      </div>
    </div>
  );
}

// ============================================================================
// Scan Progress Component - Shows scan status with stop button
// ============================================================================

function ScanProgress({ onComplete }) {
  const [progress, setProgress] = useState(null);
  const [stopping, setStopping] = useState(false);
  const sawRunning = React.useRef(false);
  const idlePolls = React.useRef(0);

  useEffect(() => {
    let interval = null;
    
    const checkProgress = async () => {
      try {
        const data = await api.fetch('/scan/progress');
        setProgress(data);
        
        if (data.status === 'running') {
          sawRunning.current = true;
          idlePolls.current = 0;
          return;
        }
        
        // Only conclude the scan is over once we've actually seen it run,
        // or after a generous grace period (the backend marks 'running'
        // synchronously now, but transient blips shouldn't kill the display)
        idlePolls.current += 1;
        if (sawRunning.current || idlePolls.current >= 5) {
          if (interval) {
            clearInterval(interval);
            if (onComplete) onComplete();
          }
        }
      } catch (err) {
        // Transient fetch error — keep polling, don't drop the display
        console.error('Failed to get scan progress:', err);
      }
    };
    
    checkProgress();
    interval = setInterval(checkProgress, 2000);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [onComplete]);

  const handleStop = async () => {
    setStopping(true);
    try {
      await api.fetch('/scan/stop', { method: 'POST' });
    } catch (err) {
      console.error('Failed to stop scan:', err);
      setStopping(false);
    }
  };

  if (!progress || progress.status === 'idle') {
    return null;
  }

  const preparing = !progress.total_items || progress.total_items === 0;

  return (
    <div className="scan-progress">
      <div className="scan-progress-header">
        <span className="scan-status">
          {stopping ? '⏳ Stopping after current item...' : preparing ? '⏳ Preparing scan...' : '🔄 Scanning...'}
        </span>
        <span className="scan-counts">
          {preparing ? '' : `${progress.processed_items} / ${progress.total_items}`}
          {!preparing && progress.percent_complete > 0 && ` (${progress.percent_complete}%)`}
        </span>
      </div>
      
      <div className={`scan-progress-bar ${preparing ? 'indeterminate' : ''}`}>
        <div 
          className="scan-progress-fill"
          style={preparing ? {} : { width: `${progress.percent_complete || 0}%` }}
        ></div>
      </div>
      
      {progress.current_item && (
        <div className="scan-current-item">
          Currently: {progress.current_item}
        </div>
      )}
      
      {!stopping && (
        <button className="stop-scan-btn" onClick={handleStop}>
          ⏹ Stop Scan
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Dashboard Component - Enhanced with download stats
// ============================================================================

function Dashboard({ stats, onRefresh, onTabChange }) {
  const [downloadStats, setDownloadStats] = useState(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    loadDownloadStats();
    checkScanStatus();
  }, []);

  const loadDownloadStats = async () => {
    try {
      const data = await api.fetch('/downloads/stats?hours=24');
      setDownloadStats(data);
    } catch (err) {
      console.error('Failed to load download stats:', err);
    }
  };

  const checkScanStatus = async () => {
    try {
      const data = await api.fetch('/scan/progress');
      setScanning(data.status === 'running');
    } catch (err) {
      console.error('Failed to check scan status:', err);
    }
  };

  const handleScanComplete = () => {
    setScanning(false);
    onRefresh();
    loadDownloadStats();
  };

  const triggerScan = async () => {
    if (!window.confirm('Start a FULL library scan? This works through a batch of ALL missing items (16k+ episodes on your library, processed in batches). To scan specific shows or movies, use Library → Scan Selected instead.')) return;
    try {
      await api.fetch('/scan', { method: 'POST' });
      setScanning(true);
    } catch (err) {
      alert('Failed to start scan: ' + err.message);
    }
  };

  return (
    <div className="dashboard">
      {scanning && (
        <ScanProgress onComplete={handleScanComplete} />
      )}
      
      <div className="stats-grid">
        <div className="stat-card clickable" onClick={() => onTabChange('approvals')}>
          <div className="stat-icon">📋</div>
          <div className="stat-value">{stats?.pending_approvals || 0}</div>
          <div className="stat-label">Pending Approvals</div>
        </div>

        <div className="stat-card clickable" onClick={() => onTabChange('downloads')}>
          <div className="stat-icon">✅</div>
          <div className="stat-value">{downloadStats?.completed || stats?.auto_downloaded_24h || 0}</div>
          <div className="stat-label">Completed (24h)</div>
        </div>

        <div className="stat-card clickable" onClick={() => onTabChange('downloads')}>
          <div className="stat-icon">⏳</div>
          <div className="stat-value">{downloadStats?.in_progress || 0}</div>
          <div className="stat-label">In Progress</div>
        </div>

        <div className="stat-card warning clickable" onClick={() => onTabChange('downloads')}>
          <div className="stat-icon">❌</div>
          <div className="stat-value">{downloadStats?.failed || 0}</div>
          <div className="stat-label">Failed (24h)</div>
        </div>
      </div>

      {downloadStats && downloadStats.total > 0 && (
        <div className="success-rate">
          <div className="rate-bar">
            <div 
              className="rate-fill success"
              style={{ width: `${downloadStats.success_rate}%` }}
            ></div>
          </div>
          <span className="rate-text">
            {downloadStats.success_rate}% success rate ({downloadStats.completed}/{downloadStats.total} completed)
          </span>
        </div>
      )}

      <div className="stats-grid secondary">
        <div className="stat-card clickable" onClick={() => onTabChange('missing')}>
          <div className="stat-icon">❓</div>
          <div className="stat-value">{stats?.missing_count || 0}</div>
          <div className="stat-label">No Releases</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🧲</div>
          <div className="stat-value">{stats?.torrent_only || 0}</div>
          <div className="stat-label">Torrent Only</div>
        </div>
      </div>

      <div className="connection-status">
        <h3>Connection Status</h3>
        <div className="status-row">
          <span className={`status-indicator ${stats?.sonarr_connected ? 'connected' : 'disconnected'}`}>
            {stats?.sonarr_connected ? '●' : '○'}
          </span>
          <span>Sonarr</span>
          <span className="status-text">
            {stats?.sonarr_connected ? 'Connected' : 'Not Connected'}
          </span>
        </div>
        <div className="status-row">
          <span className={`status-indicator ${stats?.radarr_connected ? 'connected' : 'disconnected'}`}>
            {stats?.radarr_connected ? '●' : '○'}
          </span>
          <span>Radarr</span>
          <span className="status-text">
            {stats?.radarr_connected ? 'Connected' : 'Not Connected'}
          </span>
        </div>
      </div>

      <div className="dashboard-actions">
        <button 
          className="scan-btn" 
          onClick={triggerScan}
          disabled={scanning}
        >
          {scanning ? '🔄 Scanning...' : '🔍 Start Scan'}
        </button>
      </div>

      {stats?.last_scan && (
        <div className="last-scan">
          Last scan: {new Date(stats.last_scan).toLocaleString()}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Approvals Component - Enhanced with sorting and detailed release info
// ============================================================================

function ApprovalsPage({ onRefreshStats, globalBulkJob, onDismissBulkJob }) {
  const bulkJob = globalBulkJob;
  const setBulkJob = onDismissBulkJob ? (val) => { if (!val) onDismissBulkJob(); } : () => {};
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [searchingReleases, setSearchingReleases] = useState(null);
  const [alternativeReleases, setAlternativeReleases] = useState(null);
  const [grabbingRelease, setGrabbingRelease] = useState(null);

  const searchAlternatives = async (itemId) => {
    setSearchingReleases(itemId);
    setAlternativeReleases(null);
    try {
      const data = await api.fetch(`/approvals/${itemId}/releases`);
      setAlternativeReleases(data);
    } catch (err) {
      alert('Failed to search releases: ' + err.message);
      setSearchingReleases(null);
    }
  };

  const grabAlternative = async (itemId, release) => {
    setGrabbingRelease(release.guid);
    try {
      await api.fetch(`/approvals/${itemId}/grab`, {
        method: 'POST',
        body: JSON.stringify({ release_data: release.release_data })
      });
      setAlternativeReleases(null);
      setSearchingReleases(null);
      loadApprovals();
      if (onRefreshStats) onRefreshStats();
    } catch (err) {
      alert('Failed to grab release: ' + err.message);
    } finally {
      setGrabbingRelease(null);
    }
  };

  const closeReleasesModal = () => {
    setAlternativeReleases(null);
    setSearchingReleases(null);
  };

  const loadApprovals = useCallback(async () => {
    try {
      const data = await api.fetch(
        `/approvals?page=${page}&per_page=${perPage}&sort_by=${sortBy}&sort_order=${sortOrder}`
      );
      setApprovals(data.items || []);
      setTotalPages(data.total_pages || 1);
      setSelectedItems(new Set()); // Clear selections on reload
    } catch (err) {
      console.error('Failed to load approvals:', err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortBy, sortOrder]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const handleAction = async (itemId, action) => {
    setProcessing(prev => ({ ...prev, [itemId]: true }));
    try {
      await api.fetch(`/approvals/${itemId}`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      // Remove the item from local state for immediate UX
      setApprovals(prev => {
        const remaining = prev.filter(item => item.id !== itemId);
        // If page is now empty, trigger a reload to get next batch
        if (remaining.length === 0) {
          setTimeout(() => {
            setPage(1);
            loadApprovals();
          }, 100);
        }
        return remaining;
      });
      if (onRefreshStats) onRefreshStats();
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setProcessing(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedItems.size === 0) return;
    
    setBulkProcessing(true);
    const itemIds = Array.from(selectedItems);
    
    try {
      await api.fetch('/approvals/bulk', {
        method: 'POST',
        body: JSON.stringify({ item_ids: itemIds, action }),
      });
      // Job is now running in the background — the global poller will pick it up
    } catch (err) {
      alert(`Failed to start bulk ${action}: ${err.message || err}`);
    }
    
    setSelectedItems(new Set());
    setBulkProcessing(false);
    await loadApprovals();
    if (onRefreshStats) onRefreshStats();
  };

  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === approvals.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(approvals.map(item => item.id)));
    }
  };

  const formatSize = (mb) => {
    if (!mb) return 'Unknown';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(0)} MB`;
  };

  const getOverPercentClass = (percent) => {
    if (!percent) return '';
    if (percent <= 10) return 'over-low';
    if (percent <= 30) return 'over-medium';
    return 'over-high';
  };

  if (loading) {
    return <div className="loading">Loading approvals...</div>;
  }

  if (approvals.length === 0 && !bulkProcessing) {
    return (
      <div className="empty-state">
        <div className="empty-icon">✓</div>
        <h3>All Clear!</h3>
        <p>No items waiting for approval</p>
        <button className="approve-btn" style={{marginTop: '1rem', padding: '0.5rem 1.5rem'}} onClick={() => { setPage(1); loadApprovals(); }}>
          🔄 Check for more
        </button>
      </div>
    );
  }

  return (
    <div className="approvals-page">
      {bulkJob && bulkJob.status !== 'none' && (
        <div className={`bulk-job-banner ${bulkJob.status === 'complete' ? 'complete' : 'running'}`}>
          <div className="bulk-job-banner-content">
            <span className="bulk-job-icon">{bulkJob.status === 'complete' ? '✓' : '⏳'}</span>
            <div className="bulk-job-info">
              <strong>
                {bulkJob.status === 'complete'
                  ? `Bulk ${bulkJob.action} complete`
                  : `Bulk ${bulkJob.action || 'action'} in progress…`}
              </strong>
              <span className="bulk-job-stats">
                {bulkJob.success || 0} succeeded
                {bulkJob.failed > 0 ? `, ${bulkJob.failed} failed` : ''}
                {bulkJob.status !== 'complete' && ` (${bulkJob.processed || 0} / ${bulkJob.total || 0})`}
              </span>
            </div>
            {bulkJob.status !== 'complete' && (
              <div className="bulk-job-progress-bar">
                <div className="bulk-job-progress-fill" style={{ width: `${bulkJob.percent_complete || 0}%` }} />
              </div>
            )}
            {bulkJob.status === 'complete' && (
              <button className="bulk-job-dismiss" onClick={() => { if (onDismissBulkJob) onDismissBulkJob(bulkJob.id); }}>✕</button>
            )}
          </div>
          {bulkJob.errors && bulkJob.errors.length > 0 && (
            <div className="bulk-job-errors">
              {bulkJob.errors.slice(0, 3).map((e, i) => <div key={i} className="bulk-job-error">⚠ {e}</div>)}
              {bulkJob.errors.length > 3 && <div className="bulk-job-error">…and {bulkJob.errors.length - 3} more</div>}
            </div>
          )}
        </div>
      )}
      <div className="approvals-controls">
        <div className="sort-controls">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="created_at">Date Added</option>
            <option value="title">Title</option>
            <option value="series">Series/Season/Episode</option>
            <option value="size">File Size</option>
            <option value="percent_over">% Over Limit</option>
          </select>
          <button 
            className="sort-order-btn"
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            title={sortOrder === 'desc' ? 'Descending' : 'Ascending'}
          >
            {sortOrder === 'desc' ? '↓' : '↑'}
          </button>
          <label>Show:</label>
          <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>

        {approvals.length > 0 && (
          <div className="bulk-controls">
            <label className="select-all-label">
              <input 
                type="checkbox" 
                checked={selectedItems.size === approvals.length && approvals.length > 0}
                onChange={toggleSelectAll}
              />
              Select All ({selectedItems.size}/{approvals.length})
            </label>
            {selectedItems.size > 0 && (
              <>
                <button 
                  className="bulk-approve-btn"
                  onClick={() => handleBulkAction('approve')}
                  disabled={bulkProcessing}
                >
                  {bulkProcessing ? `⏳ Processing (${selectedItems.size} left)...` : `✓ Approve (${selectedItems.size})`}
                </button>
                <button 
                  className="bulk-deny-btn"
                  onClick={() => handleBulkAction('deny')}
                  disabled={bulkProcessing}
                >
                  {bulkProcessing ? '...' : `✗ Deny (${selectedItems.size})`}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="approval-list">
        {approvals.map((item) => (
          <div key={item.id} className={`approval-card ${selectedItems.has(item.id) ? 'selected' : ''}`}>
            <div className="approval-checkbox">
              <input 
                type="checkbox" 
                checked={selectedItems.has(item.id)}
                onChange={() => toggleSelectItem(item.id)}
              />
            </div>
            <div className="approval-content">
              <div className="approval-header">
                <span className={`media-type ${item.media_type}`}>
                  {item.media_type === 'episode' ? '📺' : '🎬'}
                </span>
                <div className="title-section">
                  <h4>{item.title}</h4>
                  {item.series_title && item.media_type === 'episode' && (
                    <span className="series-info">
                      {item.series_title} S{String(item.season_number || 0).padStart(2, '0')}E{String(item.episode_number || 0).padStart(2, '0')}
                    </span>
                  )}
                </div>
              </div>

              <div className="approval-details">
                <div className="size-comparison">
                  <div className="size-bar-container">
                    <div className="size-bar limit" style={{width: '100%'}}></div>
                    <div 
                      className={`size-bar actual ${getOverPercentClass(item.percent_over)}`}
                      style={{width: `${Math.min(100 + (item.percent_over || 0), 200) / 2}%`}}
                    ></div>
                  </div>
                  <div className="size-numbers">
                    <span className="actual-size">{formatSize(item.actual_size_mb)}</span>
                    <span className={`over-percent ${getOverPercentClass(item.percent_over)}`}>
                      {item.percent_over ? `+${item.percent_over.toFixed(0)}%` : ''}
                    </span>
                    <span className="limit-size">Limit: {formatSize(item.size_limit_mb)}</span>
                  </div>
                </div>

                <p className="reason">{item.reason}</p>

                {(item.release_title || (item.release && item.release.title)) && (
                  <div className="release-filename">
                    <span className="filename-label">File:</span>
                    <code className="filename">{item.release_title || item.release?.title}</code>
                  </div>
                )}

                {item.quality_available && (
                  <div className="quality-badge">
                    Quality: {item.quality_available}
                  </div>
                )}
              </div>

              <div className="approval-actions">
                <button
                  className="approve-btn"
                  onClick={() => handleAction(item.id, 'approve')}
                  disabled={processing[item.id]}
                >
                  {processing[item.id] ? '...' : '✓ Approve'}
                </button>
                <button
                  className="search-alt-btn"
                  onClick={() => searchAlternatives(item.id)}
                  disabled={searchingReleases === item.id}
                >
                  {searchingReleases === item.id ? '🔍 Searching...' : '🔍 Alternatives'}
                </button>
                <button
                  className="deny-btn"
                  onClick={() => handleAction(item.id, 'deny')}
                  disabled={processing[item.id]}
                >
                  {processing[item.id] ? '...' : '✗ Deny'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            Next
          </button>
        </div>
      )}

      {alternativeReleases && (
        <div className="modal-overlay" onClick={closeReleasesModal}>
          <div className="modal-content releases-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔍 Alternative Releases</h3>
              <button className="modal-close" onClick={closeReleasesModal}>✕</button>
            </div>
            <div className="modal-subheader">
              <strong>{alternativeReleases.item.title}</strong>
              <span className="current-info">
                Current: {alternativeReleases.item.current_size_mb >= 1024 
                  ? `${(alternativeReleases.item.current_size_mb/1024).toFixed(2)} GB` 
                  : `${Math.round(alternativeReleases.item.current_size_mb)} MB`}
                {' '}| Limit: {alternativeReleases.item.size_limit_mb >= 1024
                  ? `${(alternativeReleases.item.size_limit_mb/1024).toFixed(2)} GB`
                  : `${Math.round(alternativeReleases.item.size_limit_mb)} MB`}
              </span>
            </div>
            {alternativeReleases.blocklisted_hidden > 0 && (
              <div className="blocklisted-hidden-note">
                🚫 {alternativeReleases.blocklisted_hidden} previously-failed (blocklisted) release{alternativeReleases.blocklisted_hidden > 1 ? 's' : ''} hidden
              </div>
            )}
            <div className="releases-list">
              {alternativeReleases.releases.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">😕</div>
                  <h3>No Alternatives Found</h3>
                  <p>No other releases available for this episode/movie. The indexers returned no additional results.</p>
                </div>
              ) : (
                alternativeReleases.releases.map((rel, idx) => (
                  <div key={idx} className={`release-card ${rel.within_limit ? 'within-limit' : 'over-limit'}`}>
                    <div className="release-info">
                      <div className="release-title-row">
                        <span className={`protocol-badge ${rel.protocol}`}>
                          {rel.protocol === 'torrent' ? '🧲' : '📡'} {rel.protocol}
                        </span>
                        <span className={`quality-badge ${rel.is_hd ? 'hd' : 'sd'}`}>
                          {rel.quality}
                        </span>
                        {rel.within_limit && (
                          <span className="fits-badge">✓ Within limit</span>
                        )}
                        {rel.below_min && (
                          <span className="below-min-badge" title="Smaller than the minimum size for this quality — often fake or heavily compressed">⚠ Below min size</span>
                        )}
                      </div>
                      <code className="release-filename">{rel.title}</code>
                      <div className="release-meta">
                        <span className="release-size">{rel.size_display}{rel.limit_mb ? ` / limit ${rel.limit_mb >= 1024 ? (rel.limit_mb/1024).toFixed(1) + ' GB' : Math.round(rel.limit_mb) + ' MB'}` : ''}</span>
                        {rel.seeders !== null && rel.seeders !== undefined && (
                          <span className="release-seeders">🌱 {rel.seeders} seeders</span>
                        )}
                        <span className="release-age">{rel.age_days}d old</span>
                        <span className="release-indexer">{rel.indexer}</span>
                      </div>
                    </div>
                    <button
                      className="grab-btn"
                      onClick={() => grabAlternative(alternativeReleases.item.id, rel)}
                      disabled={grabbingRelease === rel.guid}
                    >
                      {grabbingRelease === rel.guid ? '⏳...' : '⬇️ Grab'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Missing Reports Component
// ============================================================================

function MissingPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const loadItems = useCallback(async () => {
    try {
      const data = await api.fetch(`/missing?per_page=50&sort_by=${sortBy}&sort_order=${sortOrder}`);
      setItems(data.items || []);
    } catch (err) {
      console.error('Failed to load missing:', err);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const dismissItem = async (itemId) => {
    try {
      await api.fetch(`/missing/${itemId}`, { method: 'DELETE' });
      loadItems();
    } catch (err) {
      alert('Failed to dismiss: ' + err.message);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (items.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🎉</div>
        <h3>Nothing Missing!</h3>
        <p>All your content has available releases</p>
      </div>
    );
  }

  return (
    <div className="missing-page">
      <div className="sort-controls">
        <label>Sort by:</label>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="updated_at">Last Updated</option>
          <option value="title">Title</option>
          <option value="series">Series/Season/Episode</option>
        </select>
        <button 
          className="sort-order-btn"
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
        >
          {sortOrder === 'desc' ? '↓' : '↑'}
        </button>
      </div>

      <div className="missing-list">
        {items.map((item) => (
          <div key={item.id} className="missing-card">
            <div className="missing-header">
              <span className="media-type">
                {item.media_type === 'episode' ? '📺' : '🎬'}
              </span>
              <h4>{item.title}</h4>
            </div>
            <p className="reason">{item.reason}</p>
            <div className="missing-dates">
              {item.created_at && (
                <span className="date-info" title="First reported missing">
                  📅 First: {new Date(item.created_at).toLocaleDateString()}
                </span>
              )}
              {item.updated_at && (
                <span className="date-info" title="Last scanned and confirmed missing">
                  🔄 Last checked: {new Date(item.updated_at).toLocaleString()}
                </span>
              )}
            </div>
            <button className="dismiss-btn" onClick={() => dismissItem(item.id)}>
              Dismiss
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Downloads History Page - Shows full lifecycle with reasons and status
// ============================================================================

function DownloadsPage({ onRefreshStats }) {
  const [downloads, setDownloads] = useState([]);
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('grabbed_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [historyPerPage, setHistoryPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState('history');
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupStatus, setCleanupStatus] = useState(null);
  const [queueFilter, setQueueFilter] = useState('all');
  const [queueSearch, setQueueSearch] = useState('');
  const [toast, setToast] = useState(null);

  // Auto-dismiss toasts
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const loadDownloads = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: page,
        per_page: historyPerPage,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      if (statusFilter) params.append('status', statusFilter);
      
      const data = await api.fetch(`/downloads?${params}`);
      setDownloads(data.items || []);
      setStats(data.stats || {});
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Failed to load downloads:', err);
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortOrder, statusFilter, historyPerPage]);

  const loadQueue = useCallback(async () => {
    try {
      const data = await api.fetch('/queue');
      setQueue(data.items || []);
    } catch (err) {
      console.error('Failed to load queue:', err);
    }
  }, []);

  useEffect(() => {
    loadDownloads();
    loadQueue();
    
    // Refresh queue every 10 seconds
    const interval = setInterval(loadQueue, 10000);
    return () => clearInterval(interval);
  }, [loadDownloads, loadQueue]);

  const formatSize = (mb) => {
    if (!mb) return 'Unknown';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(0)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'grabbed': return '📥';
      case 'downloading': return '⏬';
      case 'downloaded': return '📦';
      case 'importing': return '📂';
      case 'completed': return '✅';
      case 'failed': return '❌';
      case 'import_pending': return '⏳';
      case 'import_failed': return '⚠️';
      default: return '❓';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'failed': 
      case 'import_failed': return 'status-failed';
      case 'downloading': 
      case 'importing': return 'status-active';
      default: return 'status-pending';
    }
  };

  const pollCleanupStatus = useCallback(async () => {
    try {
      const s = await api.fetch('/queue/cleanup-status');
      setCleanupStatus(s);
      setCleanupRunning(!!s.running);
      return s;
    } catch (err) {
      return null;
    }
  }, []);

  // Check on mount — a scheduled cleanup may already be running
  useEffect(() => {
    pollCleanupStatus();
  }, [pollCleanupStatus]);

  // While a cleanup runs, poll for live progress; when it finishes,
  // refresh the queue and show a summary.
  useEffect(() => {
    if (!cleanupRunning) return;
    const interval = setInterval(async () => {
      const s = await pollCleanupStatus();
      if (s && !s.running) {
        loadQueue();
        if (onRefreshStats) onRefreshStats();
        setCleanupResult({
          success: (s.errors || 0) === 0,
          message: `Cleanup finished: ${s.imported || 0} imported, ${s.removed || 0} removed, ` +
                   `${s.blocklisted || 0} blocklisted, ${s.errors || 0} errors`
        });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [cleanupRunning, pollCleanupStatus, loadQueue, onRefreshStats]);

  const retryImport = async (queueId, source) => {
    try {
      await api.fetch(`/queue/${queueId}/retry-import?source=${source}`, { method: 'POST' });
      setToast({ success: true, message: 'Import retry triggered' });
      loadQueue();
    } catch (err) {
      setToast({ success: false, message: 'Failed to retry import: ' + err.message });
    }
  };

  const removeQueueItem = async (item, blocklist) => {
    const verb = blocklist ? 'Remove AND blocklist' : 'Remove';
    if (!window.confirm(`${verb} "${item.title}"?${blocklist ? ' This release will not be grabbed again.' : ''}`)) return;
    try {
      await api.fetch(`/queue/${item.queue_id}?source=${item.source}&blocklist=${blocklist}`, { method: 'DELETE' });
      setToast({ success: true, message: blocklist ? 'Removed & blocklisted' : 'Removed from queue' });
      loadQueue();
      if (onRefreshStats) onRefreshStats();
    } catch (err) {
      setToast({ success: false, message: 'Failed to remove: ' + err.message });
    }
  };

  const runSmartCleanup = async (source = null) => {
    setCleanupResult(null);
    try {
      const url = source 
        ? `/queue/smart-cleanup?source=${source}`
        : '/queue/smart-cleanup';
      const result = await api.fetch(url, { method: 'POST' });
      if (result.status === 'already_running') {
        setCleanupResult({ success: false, message: result.message });
      } else {
        setCleanupResult({ success: true, message: result.message || 'Cleanup started' });
      }
      // Polling effect takes over button state from here
      setCleanupRunning(true);
      pollCleanupStatus();
    } catch (err) {
      setCleanupResult({ success: false, message: err.message });
      setCleanupRunning(false);
    }
  };

  const purgeBadFiles = async () => {
    if (!window.confirm('Remove all queue items with missing/invalid file extensions? This will NOT blocklist them so they can re-download with correct settings.')) return;
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      const result = await api.fetch('/queue/purge-bad-files', { method: 'POST' });
      setCleanupResult({ success: true, message: result.message || `Removed ${result.removed} bad files` });
      setTimeout(() => { loadQueue(); if (onRefreshStats) onRefreshStats(); }, 2000);
    } catch (err) {
      setCleanupResult({ success: false, message: err.message });
    } finally {
      setCleanupRunning(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading downloads...</div>;
  }

  return (
    <div className="downloads-page">
      {/* Toast notification */}
      {toast && (
        <div className={`app-toast ${toast.success ? 'success' : 'error'}`}>
          {toast.success ? '✅' : '❌'} {toast.message}
          <button className="dismiss-btn" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {/* Stats Summary */}
      {stats && (
        <div className="download-stats">
          <div className="stat-pill completed">
            <span className="stat-icon">✅</span>
            <span className="stat-count">{stats.completed || 0}</span>
            <span className="stat-name">Completed</span>
          </div>
          <div className="stat-pill in-progress">
            <span className="stat-icon">⏳</span>
            <span className="stat-count">{stats.in_progress || 0}</span>
            <span className="stat-name">In Progress</span>
          </div>
          <div className="stat-pill failed">
            <span className="stat-icon">❌</span>
            <span className="stat-count">{stats.failed || 0}</span>
            <span className="stat-name">Failed</span>
          </div>
          <div className="stat-pill total">
            <span className="stat-icon">📊</span>
            <span className="stat-count">{stats.success_rate || 0}%</span>
            <span className="stat-name">Success Rate</span>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="downloads-tabs">
        <button 
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          📜 History
        </button>
        <button 
          className={activeTab === 'queue' ? 'active' : ''}
          onClick={() => setActiveTab('queue')}
        >
          📥 Active Queue ({queue.length})
        </button>
      </div>

      {activeTab === 'queue' && (
        <div className="queue-section">
          {/* Smart Cleanup Toolbar */}
          <div className="queue-toolbar">
            <div className="toolbar-left">
              <span className="toolbar-label">🧹 Smart Queue Cleanup:</span>
              <button 
                className="cleanup-btn"
                onClick={() => runSmartCleanup()}
                disabled={cleanupRunning}
                title="Clean up both Sonarr and Radarr queues"
              >
                {cleanupRunning ? '⏳ Running...' : '🔄 Clean All'}
              </button>
              <button 
                className="cleanup-btn sonarr"
                onClick={() => runSmartCleanup('sonarr')}
                disabled={cleanupRunning}
                title="Clean up Sonarr queue only"
              >
                📺 Sonarr
              </button>
              <button 
                className="cleanup-btn radarr"
                onClick={() => runSmartCleanup('radarr')}
                disabled={cleanupRunning}
                title="Clean up Radarr queue only"
              >
                🎬 Radarr
              </button>
              <button
                className="cleanup-btn purge-bad"
                onClick={purgeBadFiles}
                disabled={cleanupRunning}
                title="Remove items with missing/hashed file extensions (SABnzbd misconfiguration). Will NOT blocklist — files can re-download."
              >
                🗑 Purge Bad Filenames
              </button>
            </div>
            {queue.filter(q => q.status === 'import_failed').length > 0 && (
              <div className="toolbar-right">
                <span className="import-failed-notice">⚠ {queue.filter(q => q.status === 'import_failed').length} import failed — use Smart Cleanup</span>
              </div>
            )}
          </div>

          {/* Cleanup Result Message */}
          {cleanupResult && (
            <div className={`cleanup-result ${cleanupResult.success ? 'success' : 'error'}`}>
              {cleanupResult.success ? '✅' : '❌'} {cleanupResult.message}
              <button className="dismiss-btn" onClick={() => setCleanupResult(null)}>×</button>
            </div>
          )}

          {/* Live cleanup progress */}
          {cleanupStatus?.running && (
            <div className="cleanup-progress">
              <div className="cleanup-progress-header">
                <span>🧹 Cleaning {cleanupStatus.current_service || 'queue'}… {cleanupStatus.processed}/{cleanupStatus.total || '?'}</span>
                <span className="cleanup-progress-counts">
                  📥 {cleanupStatus.imported} imported · 🗑 {cleanupStatus.removed} removed · 🚫 {cleanupStatus.blocklisted} blocklisted{cleanupStatus.errors > 0 ? ` · ❌ ${cleanupStatus.errors} errors` : ''}
                </span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${cleanupStatus.total ? Math.round((cleanupStatus.processed / cleanupStatus.total) * 100) : 0}%` }}
                ></div>
              </div>
            </div>
          )}

          {queue.filter(q => q.status === 'import_failed').length > 0 && (
            <div className="import-failed-warning">
              <span>⚠️ {queue.filter(q => q.status === 'import_failed').length} items failed to import - try Smart Cleanup to remove empty/stuck items</span>
            </div>
          )}

          {/* Filter + search */}
          {queue.length > 0 && (
            <div className="queue-filters">
              {(() => {
                const counts = queue.reduce((acc, q) => {
                  acc[q.status] = (acc[q.status] || 0) + 1;
                  return acc;
                }, {});
                const chips = [['all', `All (${queue.length})`]];
                ['downloading', 'import_pending', 'import_failed', 'completed', 'paused', 'failed'].forEach(s => {
                  if (counts[s]) chips.push([s, `${s.replace('_', ' ')} (${counts[s]})`]);
                });
                return chips.map(([value, label]) => (
                  <button
                    key={value}
                    className={`filter-chip ${queueFilter === value ? 'active' : ''}`}
                    onClick={() => setQueueFilter(value)}
                  >
                    {label}
                  </button>
                ));
              })()}
              <input
                type="text"
                className="queue-search"
                placeholder="🔍 Search queue..."
                value={queueSearch}
                onChange={(e) => setQueueSearch(e.target.value)}
              />
            </div>
          )}
          
          {queue.length === 0 ? (
            <div className="empty-state small">
              <p>No active downloads</p>
            </div>
          ) : (() => {
            const filteredQueue = queue.filter(q => {
              if (queueFilter !== 'all' && q.status !== queueFilter) return false;
              if (queueSearch && !(`${q.title || ''} ${q.series || ''}`).toLowerCase().includes(queueSearch.toLowerCase())) return false;
              return true;
            });
            if (filteredQueue.length === 0) {
              return (
                <div className="empty-state small">
                  <p>No items match the current filter</p>
                </div>
              );
            }
            return (
            <div className="queue-list">
              {filteredQueue.map((item) => (
                <div key={`${item.source}-${item.queue_id}`} className={`queue-item ${getStatusClass(item.status)}`}>
                  <div className="queue-item-header">
                    <span className="status-icon">{getStatusIcon(item.status)}</span>
                    <span className="source-icon" title={item.source}>{item.source === 'sonarr' ? '📺' : '🎬'}</span>
                    <span className="queue-title">{item.title}</span>
                    <span className={`status-badge ${item.status}`}>{item.status.replace('_', ' ')}</span>
                  </div>
                  
                  {item.status === 'downloading' && (
                    <div className="progress-bar">
                      <div className="progress-fill" style={{width: `${item.progress}%`}}></div>
                      <span className="progress-text">{item.progress}%{item.timeleft ? ` · ETA ${item.timeleft}` : ''}</span>
                    </div>
                  )}
                  
                  <div className="queue-details">
                    <span>{formatSize(item.size_mb)}</span>
                    <span>{item.quality}</span>
                    <span>{item.source}</span>
                  </div>
                  
                  {item.error_messages && item.error_messages.length > 0 && (
                    <div className="error-messages">
                      {item.error_messages.map((msg, i) => (
                        <div key={i} className="error-message">⚠️ {msg}</div>
                      ))}
                    </div>
                  )}
                  
                  <div className="queue-item-actions">
                    {item.status === 'import_failed' && (
                      <button 
                        className="retry-import-btn"
                        onClick={() => retryImport(item.queue_id, item.source)}
                      >
                        🔄 Retry Import
                      </button>
                    )}
                    <button
                      className="queue-action-btn remove"
                      onClick={() => removeQueueItem(item, false)}
                      title="Remove from queue (can re-download later)"
                    >
                      🗑 Remove
                    </button>
                    <button
                      className="queue-action-btn blocklist"
                      onClick={() => removeQueueItem(item, true)}
                      title="Remove AND blocklist this release so it's never grabbed again"
                    >
                      🚫 Blocklist
                    </button>
                  </div>
                </div>
              ))}
            </div>
            );
          })()}
        </div>
      )}

      {activeTab === 'history' && (
        <>
          {/* Sort and Filter Controls */}
          <div className="sort-controls">
            <label>Sort by:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="grabbed_at">Date Grabbed</option>
              <option value="title">Title</option>
              <option value="series">Series/Season/Episode</option>
              <option value="status">Status</option>
              <option value="size">Size</option>
            </select>
            <button 
              className="sort-order-btn"
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            >
              {sortOrder === 'desc' ? '↓' : '↑'}
            </button>
            
            <label>Filter:</label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="downloading">Downloading</option>
              <option value="failed">Failed</option>
              <option value="grabbed">Grabbed</option>
            </select>

            <label>Show:</label>
            <select value={historyPerPage} onChange={(e) => { setHistoryPerPage(Number(e.target.value)); setPage(1); }}>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>

          {/* Downloads List */}
          {downloads.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>No Download History</h3>
              <p>Downloads will appear here once ArrWise starts grabbing content</p>
            </div>
          ) : (
            <div className="downloads-list">
              {downloads.map((item) => (
                <div key={item.id} className={`download-card ${getStatusClass(item.status)}`}>
                  <div className="download-header">
                    <span className="status-icon">{getStatusIcon(item.status)}</span>
                    <div className="title-section">
                      <h4>{item.title}</h4>
                      {item.series_title && (
                        <span className="series-info">
                          {item.series_title} S{String(item.season_number || 0).padStart(2, '0')}E{String(item.episode_number || 0).padStart(2, '0')}
                        </span>
                      )}
                    </div>
                    <span className={`status-badge ${item.status}`}>
                      {item.status}
                    </span>
                  </div>

                  <div className="download-reason">
                    <strong>Why:</strong> {item.decision_reason}
                  </div>

                  {item.release_title && (
                    <div className="release-filename">
                      <span className="filename-label">File:</span>
                      <code className="filename">{item.release_title}</code>
                    </div>
                  )}

                  <div className="download-meta">
                    <span className="meta-item">
                      <strong>Size:</strong> {formatSize(item.size_mb)}
                      {item.size_limit_mb && (
                        <span className="size-limit"> / {formatSize(item.size_limit_mb)} limit</span>
                      )}
                      {item.percent_over > 0 && (
                        <span className="over-percent"> (+{item.percent_over.toFixed(0)}%)</span>
                      )}
                    </span>
                    {item.quality && <span className="meta-item"><strong>Quality:</strong> {item.quality}</span>}
                    {item.was_only_option && <span className="meta-badge only-option">Only Option</span>}
                    {!item.was_auto_approved && <span className="meta-badge manual">Manual</span>}
                  </div>

                  <div className="download-timeline">
                    <span className="timeline-item">Grabbed: {formatDate(item.grabbed_at)}</span>
                    {item.download_completed_at && (
                      <span className="timeline-item">Downloaded: {formatDate(item.download_completed_at)}</span>
                    )}
                    {item.import_completed_at && (
                      <span className="timeline-item">Imported: {formatDate(item.import_completed_at)}</span>
                    )}
                    {item.failed_at && (
                      <span className="timeline-item failed">Failed: {formatDate(item.failed_at)}</span>
                    )}
                  </div>

                  {item.error_message && (
                    <div className="error-message">
                      <strong>Error:</strong> {item.error_message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Activity Log Component - Enhanced with detailed reasons and release info
// ============================================================================

function ActivityPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [perPage, setPerPage] = useState(30);
  const [sortSupported, setSortSupported] = useState(true);
  const [purging, setPurging] = useState(false);

  const handlePurgeActivity = async () => {
    if (!window.confirm('Clear all activity older than 30 days?')) return;
    setPurging(true);
    try {
      await api.fetch('/purge/activity', { method: 'POST', body: JSON.stringify({ days: 30, scope: 'all' }) });
      loadActivities();
    } catch (e) { alert('Purge failed: ' + e.message); }
    finally { setPurging(false); }
  };

  const loadActivities = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: page,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      if (filterType) params.append('action_type', filterType);
      
      const data = await api.fetch(`/activity?${params}`);
      setActivities(data.items || []);
      setTotalPages(data.total_pages || 1);
      if (typeof data.sort_supported === 'boolean') setSortSupported(data.sort_supported);
    } catch (err) {
      console.error('Failed to load activity:', err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortBy, sortOrder, filterType]);

  // Jump back to page 1 whenever filters change — staying on e.g. page 7
  // of a now-2-page filtered result showed an empty list
  useEffect(() => {
    setPage(1);
  }, [filterType, perPage, sortBy, sortOrder]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const getActionIcon = (action) => {
    const icons = {
      'auto_download': '⬇️',
      'manual_approved': '✅',
      'manual_denied': '❌',
      'blocklist_add': '🚫',
      'blocklist_removed': '♻️',
      'scan_complete': '🔍',
      'approval_queued': '📋',
      'manual_import_triggered': '📂',
      'sd_fallback': '📺',
      'smart_queue_cleanup': '🧹',
      'download_retry': '🔁',
    };
    return icons[action] || '📝';
  };

  const getActionLabel = (action) => {
    const labels = {
      'auto_download': 'Auto Downloaded',
      'manual_approved': 'Approved',
      'manual_denied': 'Denied',
      'blocklist_add': 'Blocklisted',
      'blocklist_removed': 'Removed from Blocklist',
      'scan_complete': 'Scan Complete',
      'approval_queued': 'Queued for Approval',
      'manual_import_triggered': 'Import Triggered',
      'sd_fallback': 'SD Fallback Used',
      'smart_queue_cleanup': 'Queue Cleanup',
      'download_retry': 'Download Retry',
    };
    return labels[action] || action.replace(/_/g, ' ');
  };

  const getActionClass = (action) => {
    if (action.includes('approved') || action === 'auto_download') return 'action-success';
    if (action.includes('denied') || action.includes('blocklist_add') || action.includes('failed')) return 'action-error';
    if (action.includes('queued') || action === 'sd_fallback') return 'action-warning';
    if (action === 'smart_queue_cleanup') return 'action-info';
    if (action === 'download_retry') return 'action-warning';
    return 'action-info';
  };

  const formatSize = (mb) => {
    if (!mb) return '';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(0)} MB`;
  };

  if (loading) {
    return <div className="loading">Loading activity...</div>;
  }

  return (
    <div className="activity-page">
      <div className="activity-controls">
        <div className="filter-controls">
          <label>Filter:</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All Activity</option>
            <option value="auto_download">Auto Downloads</option>
            <option value="manual_approved">Approvals</option>
            <option value="manual_denied">Denied</option>
            <option value="approval_queued">Queued for Approval</option>
            <option value="smart_queue_cleanup">Queue Cleanup</option>
            <option value="download_retry">Retries</option>
            <option value="blocklist_add">Blocklisted</option>
          </select>
        </div>
        
        <div className="sort-controls">
          <label>Sort:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} disabled={!sortSupported} title={sortSupported ? '' : 'Sorting requires an updated database.py'}>
            <option value="created_at">Date</option>
            <option value="title">Title</option>
            <option value="action_type">Action Type</option>
          </select>
          <button 
            className="sort-order-btn"
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            disabled={!sortSupported}
          >
            {sortOrder === 'desc' ? '↓' : '↑'}
          </button>
          <label>Show:</label>
          <select value={perPage} onChange={(e) => setPerPage(parseInt(e.target.value))}>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>

        <button 
          className="purge-inline-btn"
          onClick={handlePurgeActivity}
          disabled={purging}
          title="Clear activity older than 30 days"
        >
          {purging ? '⏳...' : '🗑️ Clear Old'}
        </button>
      </div>

      {activities.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📜</div>
          <h3>No Activity Yet</h3>
          <p>Activity will appear here as ArrWise processes content</p>
        </div>
      ) : (
        <div className="activity-list enhanced">
          {activities.map((item) => (
            <div key={item.id} className={`activity-card ${getActionClass(item.action_type)}`}>
              <div className="activity-header">
                <span className="activity-icon">{getActionIcon(item.action_type)}</span>
                <span className={`action-badge ${getActionClass(item.action_type)}`}>
                  {getActionLabel(item.action_type)}
                </span>
                <span className="activity-time">
                  {new Date(item.created_at).toLocaleString()}
                </span>
              </div>
              
              <div className="activity-body">
                <div className="activity-title">
                  <span className="media-type-icon">
                    {item.media_type === 'episode' ? '📺' : '🎬'}
                  </span>
                  <h4>{item.title}</h4>
                </div>
                
                {item.series_title && item.media_type === 'episode' && (
                  <div className="series-info">
                    {item.series_title} S{String(item.season_number || 0).padStart(2, '0')}E{String(item.episode_number || 0).padStart(2, '0')}
                  </div>
                )}
                
                {/* Show the detailed reason/explanation */}
                {item.details && (item.details.reason || (typeof item.details.message === 'string' && item.details.message)) && (
                  <div className="activity-reason">
                    <strong>Why:</strong> {item.details.reason || item.details.message}
                  </div>
                )}
                
                {/* Show scan stats if this is a scan_complete entry */}
                {item.action_type === 'scan_complete' && item.details && item.details.message && typeof item.details.message === 'object' && (
                  <div className="activity-reason">
                    <strong>Stats:</strong> {item.details.message.auto_downloads || 0} downloaded, {item.details.message.manual_queued || 0} queued, {item.details.message.no_options || 0} no releases ({item.details.message.items_processed || 0} processed)
                  </div>
                )}

                {/* Smart queue cleanup — summary row */}
                {item.action_type === 'smart_queue_cleanup' && item.details && item.details.summary === true && (
                  <div className="activity-reason">
                    <strong>Result:</strong> {item.details.imported || 0} imported, {item.details.removed || 0} removed, {item.details.blocklisted || 0} blocklisted
                    {item.details.errors > 0 && `, ${item.details.errors} errors`}
                    {item.details.initiated_by && <span className="cleanup-by"> · by {item.details.initiated_by}</span>}
                  </div>
                )}

                {/* Smart queue cleanup — per-item row */}
                {item.action_type === 'smart_queue_cleanup' && item.details && item.details.summary === false && (
                  <>
                    <div className="activity-reason">
                      <strong>Action:</strong>{' '}
                      <span className={`cleanup-action-tag cleanup-${item.details.action}`}>
                        {item.details.action === 'imported' ? '✅ Imported' :
                         item.details.action === 'removed' ? '🗑 Removed' :
                         item.details.action === 'blocklisted' ? '🚫 Blocklisted' :
                         item.details.action}
                      </span>
                      {item.details.reason && <span className="cleanup-reason"> — {item.details.reason}</span>}
                    </div>
                    {item.details.file && (
                      <div className="release-filename">
                        <span className="filename-label">File:</span>
                        <code className="filename">{item.details.file}</code>
                      </div>
                    )}
                  </>
                )}

                {/* Download retry events */}
                {item.action_type === 'download_retry' && item.details && (
                  <div className="retry-detail">
                    {item.details.event === 'auto_retry' && (
                      <>
                        <div className="retry-row">
                          <span className="retry-tag retry-attempt">Attempt {item.details.attempt}</span>
                          <span className="retry-failed-release">❌ {item.details.failed_release}</span>
                        </div>
                        {item.details.failure_reason && (
                          <div className="retry-reason">Reason: {item.details.failure_reason}</div>
                        )}
                        <div className="retry-row retry-new">
                          <span className="retry-tag retry-grabbed">↳ Auto-grabbed</span>
                          <span className="retry-new-release">
                            {item.details.new_release}
                            {item.details.new_size_mb && (
                              <span className="retry-size"> ({item.details.new_size_mb >= 1024 ? (item.details.new_size_mb/1024).toFixed(1)+'GB' : item.details.new_size_mb+'MB'})</span>
                            )}
                          </span>
                        </div>
                      </>
                    )}
                    {item.details.event === 'escalated' && (
                      <>
                        <div className="retry-row">
                          <span className="retry-tag retry-escalated">⬆ Escalated</span>
                          <span className="retry-failed-release">Failed after {item.details.attempt} attempt{item.details.attempt !== 1 ? 's' : ''}</span>
                        </div>
                        {item.details.failure_reason && (
                          <div className="retry-reason">Last failure: {item.details.failure_reason}</div>
                        )}
                        <div className="retry-row retry-new">
                          <span className="retry-tag retry-queued">↳ Re-queued for approval</span>
                          <span className="retry-new-release">
                            {item.details.escalated_to}
                            {item.details.escalated_size_mb && (
                              <span className="retry-size"> ({item.details.escalated_size_mb >= 1024 ? (item.details.escalated_size_mb/1024).toFixed(1)+'GB' : item.details.escalated_size_mb+'MB'})</span>
                            )}
                          </span>
                        </div>
                      </>
                    )}
                    {item.details.event === 'exhausted' && (
                      <>
                        <div className="retry-row">
                          <span className="retry-tag retry-exhausted">⛔ Exhausted</span>
                          <span className="retry-failed-release">No alternatives found after {item.details.attempt} attempt{item.details.attempt !== 1 ? 's' : ''}</span>
                        </div>
                        {item.details.failure_reason && (
                          <div className="retry-reason">Last failure: {item.details.failure_reason}</div>
                        )}
                      </>
                    )}
                  </div>
                )}
                
                {/* Show release filename if available */}
                {item.release_title && (
                  <div className="release-filename">
                    <span className="filename-label">File:</span>
                    <code className="filename">{item.release_title}</code>
                  </div>
                )}
                
                {/* Show size info if available */}
                {item.size_mb && (
                  <div className="size-info">
                    <span className="size-label">Size:</span>
                    <span className="size-value">{formatSize(item.size_mb)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            ← Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main App Component
// ============================================================================

// ============================================================================
// Library Page - Browse all missing content with targeted scanning
// ============================================================================

function LibraryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('shows');
  const [expandedShow, setExpandedShow] = useState(null);
  const [expandedSeason, setExpandedSeason] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [sortMode, setSortMode] = useState('title'); // title | missing | year
  const [episodeData, setEpisodeData] = useState({});      // showId -> seasons[]
  const [loadingEpisodes, setLoadingEpisodes] = useState(null); // showId being fetched

  // Episodes are loaded lazily per show — this is what makes the library
  // list itself load in ~a second instead of minutes
  const toggleExpandShow = async (showId) => {
    if (expandedShow === showId) {
      setExpandedShow(null);
      return;
    }
    setExpandedShow(showId);
    if (episodeData[showId]) return; // already cached
    setLoadingEpisodes(showId);
    try {
      const result = await api.fetch(`/library/series/${showId}/missing-episodes`);
      setEpisodeData(prev => ({ ...prev, [showId]: result.seasons || [] }));
    } catch (err) {
      console.error('Failed to load episodes:', err);
      setEpisodeData(prev => ({ ...prev, [showId]: [] }));
    } finally {
      setLoadingEpisodes(null);
    }
  };

  const loadLibrary = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true);
    try {
      const result = await api.fetch(`/library/missing${forceRefresh ? '?refresh=true' : ''}`);
      setData(result);
    } catch (err) {
      console.error('Failed to load library:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  // Poll scan progress
  const libSawRunning = React.useRef(false);
  const libIdlePolls = React.useRef(0);
  useEffect(() => {
    if (!scanning) return;
    libSawRunning.current = false;
    libIdlePolls.current = 0;
    const interval = setInterval(async () => {
      try {
        const progress = await api.fetch('/scan/progress');
        setScanProgress(progress);
        if (progress.status === 'running') {
          libSawRunning.current = true;
          libIdlePolls.current = 0;
          return;
        }
        // Only stop once we've seen the scan actually run (or after a
        // generous grace period) — the collection phase used to read as
        // 'idle' and made the progress display vanish instantly
        libIdlePolls.current += 1;
        if (libSawRunning.current || libIdlePolls.current >= 5) {
          setScanning(false);
          setScanProgress(null);
          loadLibrary();
        }
      } catch (e) {
        // Transient error — keep polling
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [scanning, loadLibrary]);

  const toggleShowSelection = (showId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(`series:${showId}`)) {
        next.delete(`series:${showId}`);
      } else {
        next.add(`series:${showId}`);
      }
      return next;
    });
  };

  const toggleMovieSelection = (movieId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(`movie:${movieId}`)) {
        next.delete(`movie:${movieId}`);
      } else {
        next.add(`movie:${movieId}`);
      }
      return next;
    });
  };

  const toggleEpisodeSelection = (episodeId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(`episode:${episodeId}`)) {
        next.delete(`episode:${episodeId}`);
      } else {
        next.add(`episode:${episodeId}`);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (activeView === 'shows' && data) {
      const filtered = getFilteredShows();
      const allKeys = new Set(filtered.map(s => `series:${s.id}`));
      setSelectedItems(allKeys);
    } else if (activeView === 'movies' && data) {
      const filtered = getFilteredMovies();
      const allKeys = new Set(filtered.map(m => `movie:${m.id}`));
      setSelectedItems(allKeys);
    }
  };

  const clearSelection = () => setSelectedItems(new Set());

  const scanSelected = async () => {
    const items = [];
    selectedItems.forEach(key => {
      const [type, id] = key.split(':');
      items.push({ type, id: parseInt(id) });
    });
    if (items.length === 0) return;

    setScanning(true);
    try {
      await api.fetch('/library/scan', {
        method: 'POST',
        body: JSON.stringify({ items })
      });
    } catch (err) {
      alert('Failed to start scan: ' + err.message);
      setScanning(false);
    }
  };

  const sortItems = (items) => {
    const sorted = [...items];
    if (sortMode === 'missing') {
      sorted.sort((a, b) => (b.missing_count || 0) - (a.missing_count || 0) || a.title.localeCompare(b.title));
    } else if (sortMode === 'year') {
      sorted.sort((a, b) => (b.year || 0) - (a.year || 0) || a.title.localeCompare(b.title));
    } else {
      sorted.sort((a, b) => a.title.localeCompare(b.title));
    }
    return sorted;
  };

  const getFilteredShows = () => {
    if (!data || !data.shows) return [];
    let shows = data.shows;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      shows = shows.filter(s => s.title.toLowerCase().includes(q));
    }
    return sortItems(shows);
  };

  const getFilteredMovies = () => {
    if (!data || !data.movies) return [];
    let movies = data.movies;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      movies = movies.filter(m => m.title.toLowerCase().includes(q));
    }
    return sortItems(movies);
  };

  if (loading) return <div className="loading">Loading library...</div>;

  const filteredShows = getFilteredShows();
  const filteredMovies = getFilteredMovies();

  return (
    <div className="library-page">
      <div className="library-header">
        <div className="library-tabs">
          <button
            className={`lib-tab ${activeView === 'shows' ? 'active' : ''}`}
            onClick={() => { setActiveView('shows'); setExpandedShow(null); clearSelection(); }}
          >
            📺 Shows ({data?.total_shows || 0})
            <span className="tab-detail">{data?.total_missing_episodes || 0} episodes</span>
          </button>
          <button
            className={`lib-tab ${activeView === 'movies' ? 'active' : ''}`}
            onClick={() => { setActiveView('movies'); clearSelection(); }}
          >
            🎬 Movies ({data?.total_movies || 0})
          </button>
        </div>

        <div className="library-search">
          <input
            type="text"
            placeholder={`Search ${activeView}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className="library-sort"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            title="Sort order"
          >
            <option value="title">A → Z</option>
            <option value="missing">Most missing</option>
            <option value="year">Newest first</option>
          </select>
          <button
            className="library-refresh-btn"
            onClick={() => loadLibrary(true)}
            disabled={refreshing}
            title="Bypass cache and re-fetch from Sonarr/Radarr"
          >
            {refreshing ? '⏳' : '🔄'} Refresh
          </button>
        </div>
      </div>

      {data?.errors && data.errors.length > 0 && (
        <div className="library-error-banner">
          ⚠️ This list may be incomplete — {data.errors.join(' · ')}
          <button className="library-refresh-btn inline" onClick={() => loadLibrary(true)} disabled={refreshing}>
            {refreshing ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      {selectedItems.size > 0 && (
        <div className="library-action-bar">
          <span>{selectedItems.size} selected</span>
          <button className="scan-selected-btn" onClick={scanSelected} disabled={scanning}>
            {scanning ? '🔍 Scanning...' : `🔍 Scan Selected (${selectedItems.size})`}
          </button>
          <button className="select-all-btn" onClick={selectAll}>Select All</button>
          <button className="clear-btn" onClick={clearSelection}>Clear</button>
        </div>
      )}

      {scanning && scanProgress && (
        <div className="scan-progress-bar">
          <div className="progress-text">
            {(!scanProgress.total_items || scanProgress.total_items === 0)
              ? (scanProgress.current_item || 'Preparing scan…')
              : `Scanning: ${scanProgress.processed_items || 0}/${scanProgress.total_items} items${scanProgress.current_item ? ` — ${scanProgress.current_item}` : ''}`}
          </div>
          <div className="progress-bar-track">
            <div
              className="progress-bar-fill"
              style={{ width: `${scanProgress.total_items ? (scanProgress.processed_items / scanProgress.total_items * 100) : 0}%` }}
            />
          </div>
        </div>
      )}

      {activeView === 'shows' && (
        <div className="library-list">
          {filteredShows.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🎉</div>
              <h3>{searchQuery ? 'No matching shows' : 'No Missing Shows'}</h3>
              <p>{searchQuery ? 'Try a different search' : 'All episodes are available'}</p>
            </div>
          ) : (
            filteredShows.map(show => (
              <div key={show.id} className={`library-card ${expandedShow === show.id ? 'expanded' : ''}`}>
                <div className="library-card-header" onClick={() => toggleExpandShow(show.id)}>
                  <input
                    type="checkbox"
                    checked={selectedItems.has(`series:${show.id}`)}
                    onChange={(e) => { e.stopPropagation(); toggleShowSelection(show.id); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="library-card-info">
                    <h4>{show.title} {show.year ? `(${show.year})` : ''}</h4>
                    <span className="library-meta">
                      {show.missing_count} episode{show.missing_count !== 1 ? 's' : ''} missing
                      {show.network ? ` · ${show.network}` : ''}
                      {show.status ? ` · ${show.status}` : ''}
                    </span>
                  </div>
                  <span className="expand-icon">{expandedShow === show.id ? '▼' : '▶'}</span>
                </div>

                {expandedShow === show.id && (
                  <div className="library-card-detail">
                    {loadingEpisodes === show.id && (
                      <div className="episode-loading">Loading episodes…</div>
                    )}
                    {loadingEpisodes !== show.id && (episodeData[show.id] || []).length === 0 && (
                      <div className="episode-loading">No missing monitored episodes found</div>
                    )}
                    {(episodeData[show.id] || []).map(season => (
                      <div key={season.season_number} className="season-group">
                        <div
                          className="season-header"
                          onClick={() => setExpandedSeason(
                            expandedSeason === `${show.id}-${season.season_number}` ? null : `${show.id}-${season.season_number}`
                          )}
                        >
                          <span className="season-title">
                            Season {season.season_number} ({season.episodes.length} missing)
                          </span>
                          <span className="expand-icon">
                            {expandedSeason === `${show.id}-${season.season_number}` ? '▼' : '▶'}
                          </span>
                        </div>

                        {expandedSeason === `${show.id}-${season.season_number}` && (
                          <div className="episode-list">
                            {season.episodes.map(ep => (
                              <div key={ep.episode_id} className="episode-row">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.has(`episode:${ep.episode_id}`)}
                                  onChange={() => toggleEpisodeSelection(ep.episode_id)}
                                />
                                <span className="ep-number">E{String(ep.episode_number).padStart(2, '0')}</span>
                                <span className="ep-title">{ep.title || 'TBA'}</span>
                                {ep.air_date && (
                                  <span className="ep-date">{new Date(ep.air_date).toLocaleDateString()}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeView === 'movies' && (
        <div className="library-list">
          {filteredMovies.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🎉</div>
              <h3>{searchQuery ? 'No matching movies' : 'No Missing Movies'}</h3>
              <p>{searchQuery ? 'Try a different search' : 'All movies are available'}</p>
            </div>
          ) : (
            filteredMovies.map(movie => (
              <div key={movie.id} className="library-card">
                <div className="library-card-header">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(`movie:${movie.id}`)}
                    onChange={() => toggleMovieSelection(movie.id)}
                  />
                  <div className="library-card-info">
                    <h4>{movie.title} {movie.year ? `(${movie.year})` : ''}</h4>
                    <span className="library-meta">
                      {movie.runtime ? `${movie.runtime} min` : ''}
                      {movie.status ? ` · ${movie.status}` : ''}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}


function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showSettings, setShowSettings] = useState(false);
  const [stats, setStats] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [globalBulkJob, setGlobalBulkJob] = useState(null);
  const dismissedJobId = React.useRef(null);

  const dismissBulkJob = React.useCallback((jobId) => {
    dismissedJobId.current = jobId;
    setGlobalBulkJob(null);
  }, []);

  // Poll bulk job status globally so the banner shows on any tab
  useEffect(() => {
    if (!user) return;
    let autoDismissTimer = null;
    const poll = async () => {
      try {
        const job = await api.fetch('/approvals/bulk/status');
        if (!job || job.status === 'none') { setGlobalBulkJob(null); return; }
        // Don't resurface a job the user already dismissed
        if (dismissedJobId.current === job.id) return;
        setGlobalBulkJob(job);
        if (job.status === 'complete') {
          clearTimeout(autoDismissTimer);
          autoDismissTimer = setTimeout(() => {
            dismissedJobId.current = job.id;
            setGlobalBulkJob(null);
          }, 8000);
        }
      } catch (e) { /* ignore */ }
    };
    poll();
    const interval = setInterval(poll, 1000);
    return () => { clearInterval(interval); clearTimeout(autoDismissTimer); };
  }, [user]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();

    // Listen for forced logout
    const handleLogout = () => {
      setUser(null);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Poll for scan progress when scanning
  useEffect(() => {
    if (!user) return;
    
    const checkScanProgress = async () => {
      try {
        const progress = await api.fetch('/scan/progress');
        setScanProgress(progress);
        setScanning(progress.status === 'running');
      } catch (err) {
        console.error('Failed to check scan progress:', err);
      }
    };
    
    checkScanProgress();
    const interval = setInterval(checkScanProgress, 3000);
    return () => clearInterval(interval);
  }, [user]);

  const checkAuth = async () => {
    try {
      // First check if setup is needed
      const status = await api.fetch('/status');
      setSetupRequired(!status.setup_complete);

      // If we have a stored token, try to use it
      if (api.getToken()) {
        const userData = await api.fetch('/auth/me');
        setUser(userData);
      }
    } catch (err) {
      // Token invalid or expired
      api.logout();
    } finally {
      setLoading(false);
    }
  };

  const loadStats = useCallback(async () => {
    if (!user) return;
    try {
      const data = await api.fetch('/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadStats();
      const interval = setInterval(loadStats, 30000);
      return () => clearInterval(interval);
    }
  }, [user, loadStats]);

  const handleLogin = (userData) => {
    setUser(userData);
    setSetupRequired(false);
  };

  const handleLogout = () => {
    api.logout();
    setUser(null);
  };

  const triggerScan = async () => {
    if (scanning) return;
    if (!window.confirm('Start a FULL library scan? This works through a batch of ALL missing items (16k+ episodes on your library, processed in batches). To scan specific shows or movies, use Library → Scan Selected instead.')) return;
    try {
      await api.fetch('/scan', { method: 'POST' });
      setScanning(true);
    } catch (err) {
      alert('Failed to start scan: ' + err.message);
    }
  };

  const stopScan = async () => {
    try {
      await api.fetch('/scan/stop', { method: 'POST' });
    } catch (err) {
      console.error('Failed to stop scan:', err);
    }
  };

  // Refresh stats on every tab change to keep badge counts accurate
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    loadStats();
  }, [loadStats]);

  // Show loading screen
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">⚡</div>
        <div className="loading-text">ArrWise</div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return (
      <LoginPage
        onLogin={handleLogin}
        setupRequired={setupRequired}
        onSetupComplete={() => setSetupRequired(false)}
      />
    );
  }

  // Show settings page
  if (showSettings) {
    return <SettingsPage onBack={() => setShowSettings(false)} />;
  }

  // Main app
  return (
    <AuthContext.Provider value={{ user, logout: handleLogout }}>
      <div className="app">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">
              <span className="logo-icon">⚡</span>
              ArrWise
            </h1>
          </div>
          
          {/* Scan Progress Indicator */}
          {scanning && scanProgress && (
            <div className="header-scan-progress">
              <div className="scan-mini-progress">
                <span className="scan-status-text">
                  {(!scanProgress.total_items || scanProgress.total_items === 0)
                    ? (scanProgress.current_item || 'Preparing scan…')
                    : `Scanning: ${scanProgress.processed_items}/${scanProgress.total_items}`}
                </span>
                <div className="scan-mini-bar">
                  <div 
                    className="scan-mini-fill" 
                    style={{width: `${scanProgress.percent_complete || 0}%`}}
                  ></div>
                </div>
              </div>
              <button className="stop-scan-mini-btn" onClick={stopScan} title="Stop scan">
                ⏹
              </button>
            </div>
          )}
          
          <div className="header-actions">
            <button 
              className={`scan-btn ${scanning ? 'scanning' : ''}`} 
              onClick={triggerScan}
              disabled={scanning}
              title="Scans ALL missing items in batches — use Library > Scan Selected for specific items"
            >
              {scanning ? '🔄 Scanning...' : '🔍 Full Scan'}
            </button>
            <button className="settings-btn" onClick={() => setShowSettings(true)}>
              ⚙️
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              👋
            </button>
          </div>
        </header>

        <nav className="nav">
          <button
            className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleTabChange('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={`nav-btn ${activeTab === 'approvals' ? 'active' : ''}`}
            onClick={() => handleTabChange('approvals')}
          >
            Approvals
            {stats?.pending_approvals > 0 && (
              <span className="badge">{stats.pending_approvals}</span>
            )}
          </button>
          <button
            className={`nav-btn ${activeTab === 'downloads' ? 'active' : ''}`}
            onClick={() => handleTabChange('downloads')}
          >
            Downloads
          </button>
          <button
            className={`nav-btn ${activeTab === 'missing' ? 'active' : ''}`}
            onClick={() => handleTabChange('missing')}
          >
            Missing
          </button>
          <button
            className={`nav-btn ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => handleTabChange('activity')}
          >
            Activity
          </button>
          <button
            className={`nav-btn ${activeTab === 'library' ? 'active' : ''}`}
            onClick={() => handleTabChange('library')}
          >
            Library
          </button>
        </nav>

        <main className="main">
          {globalBulkJob && globalBulkJob.status !== 'none' && activeTab !== 'approvals' && (
            <div className={`bulk-job-banner global-banner ${globalBulkJob.status === 'complete' ? 'complete' : 'running'}`}>
              <div className="bulk-job-banner-content">
                <span className="bulk-job-icon">{globalBulkJob.status === 'complete' ? '✓' : '⏳'}</span>
                <div className="bulk-job-info">
                  <strong>
                    {globalBulkJob.status === 'complete'
                      ? `Bulk ${globalBulkJob.action} complete`
                      : `Bulk ${globalBulkJob.action || 'action'} running in background…`}
                  </strong>
                  <span className="bulk-job-stats">
                    {globalBulkJob.success || 0} succeeded
                    {globalBulkJob.failed > 0 ? `, ${globalBulkJob.failed} failed` : ''}
                    {globalBulkJob.status !== 'complete' && ` (${globalBulkJob.processed || 0} / ${globalBulkJob.total || 0})`}
                  </span>
                </div>
                {globalBulkJob.status !== 'complete' && (
                  <div className="bulk-job-progress-bar">
                    <div className="bulk-job-progress-fill" style={{ width: `${globalBulkJob.percent_complete || 0}%` }} />
                  </div>
                )}
                <button className="bulk-job-dismiss" onClick={() => dismissBulkJob(globalBulkJob.id)}>✕</button>
              </div>
            </div>
          )}
          {activeTab === 'dashboard' && (
            <Dashboard
              stats={stats}
              onRefresh={loadStats}
              onTabChange={handleTabChange}
            />
          )}
          {activeTab === 'approvals' && <ApprovalsPage onRefreshStats={loadStats} globalBulkJob={globalBulkJob} onDismissBulkJob={(jobId) => dismissBulkJob(jobId)} />}
          {activeTab === 'downloads' && <DownloadsPage onRefreshStats={loadStats} />}
          {activeTab === 'missing' && <MissingPage />}
          {activeTab === 'activity' && <ActivityPage />}
          {activeTab === 'library' && <LibraryPage />}
        </main>

        {/* PWA Install Prompt */}
        <InstallPrompt />
      </div>
    </AuthContext.Provider>
  );
}

// ============================================================================
// PWA Install Prompt Component
// ============================================================================

function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if already installed or dismissed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed
    }

    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSince = (Date.now() - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return; // Don't show for 7 days after dismiss
    }

    // Show prompt after a short delay
    const timer = setTimeout(() => {
      if (window.deferredPrompt) {
        setShowPrompt(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleInstall = async () => {
    if (!window.deferredPrompt) return;

    window.deferredPrompt.prompt();
    const { outcome } = await window.deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
    }
    
    window.deferredPrompt = null;
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_install_dismissed', new Date().toISOString());
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div className="install-prompt">
      <div className="install-content">
        <span className="install-icon">📱</span>
        <div>
          <strong>Install ArrWise</strong>
          <p>Add to home screen for quick access</p>
        </div>
      </div>
      <div className="install-actions">
        <button onClick={handleDismiss}>Later</button>
        <button onClick={handleInstall} className="primary">Install</button>
      </div>
    </div>
  );
}

export default App;
